import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import gsap from "gsap";

import {
  WheelGestureController,
  type CardScrollTarget,
  type SnapRequestResult,
} from "./wheelGestureController";
import { isTypingTarget } from "../../utils/keyboardShortcuts";

// ===== Types =====

export interface RadioCardGroupItem {
  id: string | number;
}

export interface RadioCardGroupRenderState {
  /** 親の `value` と一致しているか（border 表現等に利用） */
  checked: boolean;
  /** 表示順 index（0-origin） */
  index: number;
  /** 現在の "中央寄せ対象" index（ホイール上端 / 選択位置のカード） */
  activeIndex: number;
}

export interface RadioCardGroupProps<T extends RadioCardGroupItem> {
  items: T[];
  /** 確定済みの選択 ID。 */
  value: T["id"] | undefined;
  /** 選択確定時に呼ばれる。 */
  // eslint-disable-next-line no-unused-vars
  onChange: (id: T["id"], item: T) => void;
  /**
   * Enter キーまたはカードのダブルクリックで「決定」したときに呼ばれる。
   * シングルクリック / スクロールは選択（onChange）のみで、画面遷移などの
   * 決定操作は Enter・ダブルクリック・呼び出し側の明示的なボタンに限定する
   * ための分離コールバック。Enter とダブルクリックは常に同じ扱いにする
   * （ホーム / プロジェクト / オファーウィザードで操作感を揃えるため）。
   */
  // eslint-disable-next-line no-unused-vars
  onConfirm?: (id: T["id"], item: T) => void;
  /**
   * Stage の余白（カード以外の領域）をクリックされたときに「無選択へ戻す」コールバック。
   * 旧 FollowsCarousel が `CarouselWrapper` のクリックで実装していた挙動の arc 版。
   * 指定しなければ deselect は起きない（純粋な radio）。
   */
  onDeselect?: () => void;
  /**
   * 各カードの中身を描画する render prop。
   * 位置 / 回転 / スケール / z-index は本コンポーネントが Slot 側に書き込むため、
   * renderCard 側ではサイズ（width/height: 100%）と内部レイアウトだけ気にすればよい。
   */
  // eslint-disable-next-line no-unused-vars
  renderCard: (item: T, state: RadioCardGroupRenderState) => React.ReactNode;
  /** カードサイズ。fan モード（progress=1）での表示サイズの基準。 */
  cardSize: { width: number; height: number };
  /**
   * スクロールの "スクローラ" 要素。未指定なら最寄りの
   * `[data-offer-overlay-scroll]` を自動で参照し、それも無ければ `window`。
   */
  scroller?: HTMLElement | null;
  /**
   * 扇形モード（progress=1）の半径(px)。
   * 明示指定しない場合は Stage の実幅から自動算出する。
   */
  fanRadius?: number;
  /**
   * カード数が `circleThreshold` 未満（少数カード）のときに、initial 配置で
   * 採用する角度的な広さ(deg)。多くのカードでは 360°（全周）になるので未使用。
   * 既定: 200（半円より少し広い扇で開始する。要件: 「5 枚以下は半円扇形」）
   */
  fanSpanDeg?: number;
  /** 扇形モード時のカード縮小率（1=等倍）。 既定: 0.75 */
  scaleFan?: number;
  /** 全周 360° 円モード時のカード縮小率。 既定: 0.32 */
  scaleCircle?: number;
  /**
   * カード数が閾値以上のとき、progress=0 を全周 360° で開始する。
   * 未満なら fanSpanDeg のままの "扇形" で開始（カードが少なく円だと隙間が大きい場合の救済）。
   * 既定: 6
   */
  circleThreshold?: number;
  /** 円 → 扇 のモーフィングに使うスクロール距離(px)。 既定: 480 */
  morphDistance?: number;
  /** ホイール回転で activeIndex を 1 進めるスクロール距離(px)。 既定: 140 */
  navStepPx?: number;
  /** 最後のカードを過ぎてから Stage が unstick するまでのバッファ(px)。 既定: 80 */
  endBufferPx?: number;
  /**
   * 親の `max-width` / `overflow: hidden` を超えて、利用可能な右カラム領域いっぱい
   *（`.dm-two-column-wrapper` 内で LeftColumn の右端 ～ viewport 右端）まで
   * Stage を広げて描画する。`position: fixed` で実装するため、Stage の縦位置は
   * JS で計算して同期する（sticky 相当の挙動）。
   * 既定: false（親の幅制限内で描画）。
   */
  breakOut?: boolean;
  /**
   * breakOut 時の Stage と viewport（/ LeftColumn 右端）との水平余白(px)。
   * Stage は `overflow: hidden` なので、この値がそのままカードの左右クリップ位置に
   * なる。0 でカード列が viewport の左右端まで到達するフルブリード表示。
   * 既定: 16。
   */
  breakOutInsetPx?: number;
  /**
   * breakOut 時にアクティブカード（θ=0）の中心を合わせる viewport Y 座標(px)。
   * 未指定なら従来どおり viewport 縦中央（`window.innerHeight / 2`）。
   * 下部に詳細パネル等の固定 UI を重ねる画面（プロジェクトリスト等）で、
   * カードをその分上へ寄せるために使う。
   */
  verticalCenterY?: number;
  /**
   * カード配置レイアウト（試作バリエーション用）。
   *  - `"arc"`（既定）: 従来の 円 → 扇 → ホイール回転（円弧配置）。
   *  - `"horizontal"`: カードを一直線の横並びに配置し、スクロール量を水平移動に
   *    読み替える完全な横スクロール。回転・円弧・モーフ演出は行わない。
   *    無限ループ・中央スナップ・「中央カード = 選択」同期・クリック移動は
   *    arc と同じ仕組みをそのまま使う。breakOut 時のタッチは水平スワイプで送る。
   */
  layout?: "arc" | "horizontal";
  /**
   * スクロール速度に応じてカード群全体をズームアウトし、停止すると等倍へ戻す演出。
   * 速く送るほど引きの絵になり、カードの全体量を掴みやすくする。
   * arc / horizontal の両レイアウトで有効。未指定で無効。
   * 実効下限は minScale をそのまま使わず、horizontal ではループ境界が、
   * arc ではホイール裏側（逆さカード）が画面に入らない範囲へ自動クランプされる。
   */
  velocityZoom?: {
    /**
     * 最大ズームアウト時の倍率（0-1）。既定: 0.5。
     * 0 を渡すと自動クランプの下限（ループ境界 / ホイール裏側が見えない限界）
     * まで引ける = 最大ズームアウト。
     */
    minScale?: number;
    /**
     * この速度(px/ms)で minScale（フル深度）に到達する。既定: 2.5。
     * 応答は凹型カーブ（VELOCITY_ZOOM_RESPONSE_EXPONENT = 0.5 の平方根）で、
     * この 1/4 の速度でも強度 50% に達する（軽いスクロールから効き始める）。
     */
    maxVelocity?: number;
  };
  /**
   * 選択中（中央）カードの強調倍率。動画ストリーミング UI のように中央の
   * カードだけ少し大きく表示する。スクロール中は中心からの距離 |k| に応じて
   * 減衰し、隣のカードへ連続的に受け渡される。
   * `layout="horizontal"` のときのみ有効。1 または未指定で無効。
   */
  activeCardScale?: number;
  /**
   * 中央カードへの「カード内スクロール引き渡し」を有効にする。
   * true のとき、中央にナッジ（settled）した後の入力は選択中カードの
   * `data-card-scroll` 要素へルーティングされ、末端では「ため（detent）」を
   * 挟んでからホイール回転へ戻る。breakOut モードでのみ機能する。
   * カード内スクロールを持たない（`data-card-scroll` が無い / スクロール
   * 不能な）カードでは「ため」を挟まず即ホイール回転に戻るため、常時有効に
   * しても操作を阻害しない。
   * 既定: false（settle 検知のみ行い、settle 後の入力は即ホイール回転）。
   */
  cardScrollHandoff?: boolean;
  /**
   * Stage の外（body ポータルのオーバーレイ等）にある要素を、このホイールの
   * ジェスチャ面として扱うためのキー。`data-wheel-gesture-surface="<key>"` を
   * 持つ要素の上で始まった wheel / touch 操作をホイール回転として受け付ける
   * （待機ショットチップのような pointer-events: auto のオーバーレイが
   * スワイプを奪ってしまう問題への対処）。breakOut モード専用。
   * 未指定なら Stage 上の操作のみ受け付ける。
   */
  gestureSurfaceKey?: string;
  /**
   * 中央ナッジ完了状態（settled）の変化通知。カードが最寄りステップへ
   * 整列しきったときに true、スクロールが再開したときに false。
   * メッセージ雲のフェードインや選択中カードの拡大演出の起点に使う。
   * breakOut モードでのみ発火する（reducedMotion / カード1枚のときは
   * 常時 true を通知する）。
   */
  // eslint-disable-next-line no-unused-vars
  onSettleChange?: (settled: boolean) => void;
  /**
   * Shift キーを押している間、全カードを中央位置へ「さっと」集約して
   * 全体量を見渡すスタックプレビュー。layout に応じて形状が変わる:
   *  - `"horizontal"`: 回転なしの横スタック（z-index は右が上）。
   *  - `"arc"`: 回転半径をカード高さ程度まで縮めた手札状の扇。
   * 表示中も ← / → で選択を送れる。通常時と同じく選択カードが中央に留まった
   * まま山全体が 1 枚ぶんずつ回転し、onChange も追従する（中央のカードは
   * わずかにせり上げて示す）。Shift を離すと、そのとき選択中のカードを中央に
   * したホイールへ戻る。指定したときのみ有効（オプトイン）。
   * 入力欄フォーカス中・修飾キー併用・キーリピートでは発動しない。
   * カードが 1 枚以下のときと disabled 時は無効。
   */
  stackPreview?: {
    /**
     * 山の下に出す件数ラベルの枚数。省略時は items.length
     * （「新規作成」「待機中」のようなセンチネルカードを除きたい場合に指定）。
     */
    count?: number;
  };
  /**
   * スタックプレビューの開始 / 解除通知。ページ側で下部パネルやメッセージ雲を
   * 隠すために使う。
   */
  // eslint-disable-next-line no-unused-vars
  onStackChange?: (active: boolean) => void;
  /**
   * ← / → キー相当の「1 step 移動」（advanceScrollByOneStep）を Stage の外から
   * 呼び出すための ref。キーボードショートカット（⌥2 再押下 = 次のプロジェクト）
   * のように、Stage にフォーカスが無い状態でもホイールを進めたい場合に使う。
   */
  advanceStepRef?: React.MutableRefObject<
    // eslint-disable-next-line no-unused-vars
    ((direction: 1 | -1) => void) | null
  >;
  /**
   * ← / →（カード送り）と Enter（決定 = onConfirm）を Stage のフォーカス無し
   * でも効かせる（window レベルで受ける）。ページ遷移直後（フォーカスが body）
   * でもホイールを操作できるようにするためのオプトイン。入力欄フォーカス中・
   * 修飾キー併用・**重ねて開いたモーダル**表示中は反応しない（自分を内包する
   * `role="dialog"` は「自分の居場所」なので阻害要因にしない。{@link hasBlockingDialog}）。
   * Stage フォーカス時は従来の onKeyDown に任せる。Enter はさらに「フォーカスが
   * body のとき」だけ反応する（ボタン等にフォーカスがあるときはその要素の決定を優先）。
   * ↑ / ↓ はページスクロールを、Space はページスクロールと入力を奪わないよう
   * グローバル化しない。Esc は各画面の「戻る」が別途 window レベルで処理して
   * いるため対象外。既定: false。
   */
  globalKeyboardNav?: boolean;
  /** 操作不能化（無効化）。 */
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * Stage 外のオーバーレイ（body ポータル等）をホイールのジェスチャ面として
 * 登録する data 属性名。値には `gestureSurfaceKey` と同じキーを入れる。
 */
export const WHEEL_GESTURE_SURFACE_ATTR = "data-wheel-gesture-surface";

// ===== ジェスチャの軸判定（純関数・テスト対象） =====

/** タッチ開始後、この距離を超えたら支配軸を確定する。 */
const TOUCH_AXIS_LOCK_THRESHOLD_PX = 6;

/**
 * タッチ開始点からの移動量（movedX / movedY）で支配軸を判定する。
 * どちらの軸も threshold 未満なら null（軸未確定 = まだ入力しない）。
 * arc / horizontal 共通で、上下左右のどのスワイプでもホイールを送れる。
 */
export const resolveTouchAxis = (
  movedX: number,
  movedY: number,
  thresholdPx: number = TOUCH_AXIS_LOCK_THRESHOLD_PX,
): "x" | "y" | null => {
  if (Math.max(Math.abs(movedX), Math.abs(movedY)) < thresholdPx) {
    return null;
  }
  return Math.abs(movedX) >= Math.abs(movedY) ? "x" : "y";
};

/**
 * wheel イベントの deltaX / deltaY から「支配的な軸」の移動量を返す。
 * 縦横どちらの操作でも「上 / 左 = 戻る（負）、下 / 右 = 進む（正）」になる。
 */
export const dominantWheelDelta = (deltaX: number, deltaY: number): number =>
  Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;

// ===== Math =====

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** power2.inOut 相当。スクロール進行のイージング用。 */
const easeInOut = (t: number) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

/** angle を (-180, 180] の範囲へ正規化する。 */
const normalizeAngle = (deg: number) => {
  const m = ((deg + 180) % 360 + 360) % 360;
  return m - 180;
};

// ===== Styled =====

/**
 * スタックプレビューの件数ラベルの z-index。山の最前面カード
 * （1000 + N/2 程度）より確実に前へ。styled 定義（モジュール評価時）で
 * 参照するため、他のスタック定数群より前にここで宣言する。
 */
const STACK_LABEL_Z_INDEX = 1500;

const Wrapper = styled.div<{ $explicitHeight: number | null }>`
  position: relative;
  width: 100%;
  ${({ $explicitHeight }) =>
    $explicitHeight !== null &&
    css`
      height: ${$explicitHeight}px;
    `}
`;

/**
 * 通常モード（$breakOut=false）: `position: sticky` で「scroller の top に張り付き」。
 * 真下の `ScrollSpacer` がピン留めスクロール距離を確保する。
 *
 * breakOut モード（$breakOut=true）: `position: fixed`。親の max-width / overflow:hidden を
 * バイパスして viewport 座標で描画する。`top` / `left` / `width` は JS が毎フレーム更新する。
 */
const Stage = styled.div<{ $height: number; $breakOut: boolean }>`
  ${({ $breakOut }) =>
    $breakOut
      ? css`
          position: fixed;
          /* top / left / width は inline style で JS が毎フレーム更新する。 */
          /*
           * sticky 表示する FilterChipBar / WizardActionPanel など (z=3) より下層に
           * 置く。これらが Stage の上に重なってホイール上下端のカードを覆う設計。
           */
          z-index: 2;
          /* JS が wheel / touch を scroller へ転送する。ブラウザのデフォルト pan
             処理と競合させないために、ここでは touch ジェスチャを完全に明け渡す。 */
          touch-action: none;
        `
      : css`
          position: sticky;
          top: 0;
          width: 100%;
          z-index: 1;
          touch-action: pan-y;
        `}
  height: ${({ $height }) => $height}px;
  /* 扇形モードで wheel の "back" 側にあるカード（off-stage）を確実にクリップ。 */
  overflow: hidden;
  user-select: none;

  /*
   * カードクリック直後の programmatic focus（キーボード操作を Stage で受けるため）で、
   * グローバル CSS の \`[tabindex="0"]:focus-visible\` による soremo-blue の outline と
   * box-shadow（薄い青影）が Stage 全体（カードの外側）に出てしまう。\`&&\` で
   * specificity を上げて確実に両方抑止する。選択中の見た目はカード側
   *（renderCard の state.checked）でのみ表現する。
   */
  &&:focus,
  &&:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

/**
 * velocityZoom 用に Slot 群をまとめて包む中間レイヤー。
 * Stage（クリップ枠）の大きさはそのままに、このレイヤーだけを scale することで
 * 「引きの絵」を作る（カード個別の scale ではピッチが変わらず可視枚数が増えないため）。
 * transform は rAF ループが inline style で直接更新する（Slot の GSAP transform とは
 * 別要素なので衝突しない）。transform-origin はアクティブカード中心
 * （水平 50% / 縦 cardCenterY）に合わせ、中央のカードを動かさずに周囲を寄せる。
 *
 * DOM 構造を安定させるため velocityZoom 無効時（未指定 / reducedMotion 等）も
 * 常に描画する。その場合 transform は書き込まれないので、合成レイヤー化のヒント
 * （will-change）だけを有効時に限定してコストを避ける。
 */
const ZoomLayer = styled.div<{ $originY: number; $zoomEnabled: boolean }>`
  position: absolute;
  inset: 0;
  transform-origin: 50% ${({ $originY }) => $originY}px;
  will-change: ${({ $zoomEnabled }) => ($zoomEnabled ? "transform" : "auto")};
`;

const ScrollSpacer = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  pointer-events: none;
`;

const Slot = styled.div<{
  $width: number;
  $height: number;
  $offsetTop: number;
}>`
  position: absolute;
  top: ${({ $offsetTop }) => $offsetTop}px;
  left: 50%;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  /* 初期マウント時のチラ見え抑止用。useLayoutEffect 内の gsap.set が上書きする。 */
  transform: translate(-50%, -50%);
  transform-origin: center center;
  will-change: transform, z-index;
  cursor: pointer;
  outline: none;

  /*
   * 以前 :focus-visible で青いリングを出していたが、マウスでクリックされた Slot が
   * フォーカスを保持して "選択は別カードに移ったのに青ボーダーが残って見える" 状態に
   * なるため撤去。選択中表示は renderCard 側の state.checked で行う。
   */

  &[aria-disabled="true"] {
    cursor: default;
    pointer-events: none;
  }
`;

/**
 * スタックプレビュー中に山の下へ出す件数ラベル。数字のみを、カード側の連番
 * （Counter の TodoCounter）と同じ DIN 2014 ExtraBold 64px で置く。
 * opacity / visibility はスタック進行度のトゥイーンが inline style で
 * 毎フレーム更新する（初期は不可視）。velocityZoom（ZoomLayer の scale）の
 * 影響を受けないよう Stage 直下に置く。
 */
const StackCountLabel = styled.div<{ $top: number }>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${STACK_LABEL_Z_INDEX};
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;

  font-family: "din-2014", sans-serif;
  font-weight: 800;
  font-style: normal;
  font-size: 64px;
  line-height: 100%;
  letter-spacing: 0.08em;
`;

// ===== Defaults =====

// 6 枚以上で 360° 円配置、5 枚以下は半円扇形のままとする（要件: カード5枚以下は半円）。
const DEFAULT_CIRCLE_THRESHOLD = 6;
const DEFAULT_FAN_SPAN_DEG = 200;

/**
 * カード数がこの値未満のときは初期アニメーション（円→扇モーフ + 入場フェード）を
 * 省略し、最初から扇形でスワイプ可能にする。
 */
const SMALL_CARD_SKIP_INTRO_MAX = 5;
/**
 * R_fan auto 算出で「Stage 内に収まってほしい」可視カードの枚数。
 * これと pitchDeg(=360/N or fanSpanDeg/N) から visibleHalfSpanDeg を導出して R を逆算する。
 * 値が小さいほど "なだらか" な曲線になる（隣接 1 枚ずつ可視）。
 */
const FAN_VISIBLE_COUNT_FOR_RADIUS = 3;
const DEFAULT_SCALE_CIRCLE = 0.32;
const DEFAULT_SCALE_FAN = 0.75;
const DEFAULT_MORPH_DISTANCE = 480;
const DEFAULT_NAV_STEP_PX = 140;
const DEFAULT_END_BUFFER_PX = 80;
/**
 * 無限スクロール用に、ホイールを何周ぶんスクロールさせるか。
 * 多いほど "止まる感じ" が薄れる。
 */
const INFINITE_SCROLL_LOOPS = 50;
/**
 * 下方向の自然ループのために、scrollTop をステルス巻き戻しするトリガ周回数。
 * `fractionalCenter` が `LOOP_ANCHOR_LOOPS * N + N` を超えたら scrollTop を
 * `N * navStepPx` ぶん減算し、scroller を「ループ周期の手前」に戻す。
 * `pitchDeg = 360/N` なので mod N で視覚位置 / activeIndex は変わらない。
 *
 * INFINITE_SCROLL_LOOPS の中央値付近に置くことで、巻き戻し後も末端 / 頂点まで
 * それぞれ同じだけ余裕が残る。
 */
const LOOP_ANCHOR_LOOPS = 25;
/**
 * Phase 2 に初回到達した直後に、scrollTop を「中央周回」までステルス前進させる
 * 周回数。これにより `currentStep` が常に大きい値で始まり、左右キー切り替え時の
 * `currentStep === 0 → wrap で前方向 N-1 step 大回り` 現象（=「逆に戻る」）を
 * 回避できる。両方向に同じだけ余裕を持たせるため LOOP_ANCHOR_LOOPS の半分とする。
 */
const LOOP_INITIAL_ADVANCE_LOOPS = Math.floor(LOOP_ANCHOR_LOOPS / 2);
/**
 * カード移動（ホイール回転）が始まった後、半径を fanRadius の何倍まで広げるか。
 * 1.0 なら拡張しない。1.5 なら 50% 広い円になる。
 *
 * ただし viewport 高さが取得できているときは `ROTATION_R_VIEWPORT_RATIO * viewportH`
 * を優先する（カード数が多く fanRadius がコンパクトだと、倍率だけでは弧が
 * なだらかにならないため）。これは fallback 値。
 */
const ROTATION_R_MULTIPLIER = 1.5;
/**
 * 移動モード（Phase 2、wheel 回転中）で目指す半径を viewport 高さの何倍にするか。
 * 大きいほど弧度がなだらかになり「ほぼ直線」に近い扇形になる。
 * 要件: 「移動モードになったときは、より扇形になるよう円径をさらに広げる
 *        （viewport の 2 倍ほど）」
 */
const ROTATION_R_VIEWPORT_RATIO = 2.0;
/**
 * R 拡張が完了するまでのカード advance 量。
 * 1 にすると最初の 1 枚送りで完全に広がる。
 */
const ROTATION_R_RAMP_STEPS = 1;
/**
 * Phase 1 終端（扇形着地時）の R クランプの下限比率。
 * 隣接カード中心距離 / カード幅 がこれより小さくなる（= 50% 以上重なる）ことを許さない。
 * Phase 2 経路では参照しない（中央は `BASE_GAP_RATIO` で詰まる前提）。
 */
const MIN_GAP_RATIO = 0.5;
/**
 * Phase 1 終端（扇形着地時）の R クランプの上限比率。
 * 隣接カード中心距離 / カード幅 がこれより大きくなる（= 200% 以上広がる）ことを許さない。
 * Phase 2 経路では参照しない（θ は `BASE_GAP_RATIO` + 指数多項式累積で別に算出される）。
 */
const MAX_GAP_RATIO = 2.0;
/**
 * Phase 2（rotation 中）で中央付近のカード間中心距離 / カード幅の base 比率。
 *
 * 純粋な「カード同士がぴったり接する」値は 1.0 だが、各カードは θ_i ぶん回転して
 * 描画されるため（回転後の visual 幅 ≈ w * cos(θ) + h * sin(θ) で w を上回る）、
 * 回転による重なりを吸収するために 1.0 より少し大きい値を既定とする。
 * 値が大きいほどカード間が広がり、小さいほど中央密度が増す（重なり始める）。
 *
 * `basePitchDeg = 2 * asin(BASE_GAP_RATIO * wEff / (2R))` で中央 pitch を逆算し、
 * 累積関数で θ_i を求める（`FAN_DISTRIBUTION_POWER` で外側の加速度を制御）。
 */
const BASE_GAP_RATIO = 1.15;
/**
 * Phase 2 の累積関数で「外側に進むほど pitch が広がる」非線形項の指数。
 *  θ(k) = sign(k) * [basePitch * |k| + (180 - basePitch * halfN) * (|k| / halfN)^p]
 *
 *  - p=1: 線形（均等配置と同等）
 *  - p=2: 中央緩やか、端でほどよく加速
 *  - p=3 (既定): 中央が長く線形に保たれ、端で滑らかに加速して画面奥に到達
 *  - 大きいほど中央密集（線形に近い）、外側に行くほど急激にループ境界（±180°）に到達
 *
 * ループ境界（|k| = halfN）でちょうど 180° に到達するよう非線形項のスケールを
 * 自動調整しているため、`k` のラップ点で θ が ±180° で連続になり視覚ジャンプは起きない。
 */
const FAN_DISTRIBUTION_POWER = 3;

const STAGE_PADDING = 24;
/** Stage の左右端と端カードとの最小水平余白。 */
const STAGE_EDGE_INSET = 16;
/** 半径が未測定 / Stage 幅 0 のときの仮値。 */
const FALLBACK_RADIUS = 240;
/** auto 算出時の半径下限（極端に狭い Stage 対策）。 */
const MIN_AUTO_RADIUS = 120;
/**
 * 円モードで隣り合うカード同士に空ける比率。
 * (1.0 = カード同士が触れる、>1 = 隙間あり)
 */
const CIRCLE_CARD_GAP_FACTOR = 1.3;
/** breakOut 時の Stage と viewport / LeftColumn との水平余白(px)。 */
const BREAKOUT_HORIZONTAL_INSET = 16;
/**
 * クリック（`scrollToCardIndex`）で smooth-scroll を発火した後、scroll listener 経由の
 * activeIndex 同期を一時停止する時間(ms)。target 到達まで中間 round 値で activeIndex が
 * チラつくのを防ぐ。
 *
 * キーボード（`advanceScrollByOneStep`）はロックを立てず、scroll listener が
 * 「視覚中央 = 選択」を常時同期する（押しっぱなしのキーリピートで activeIndex と
 * 視覚位置が乖離するのを防ぐため）。
 */
const SCROLL_SYNC_LOCK_MS = 600;

/**
 * 入場アニメーション（"左から円を描くように" カードを順次フェードイン）の各 tween 設定。
 *  - `ENTRY_STAGGER_SEC`: 隣接カードの開始時間差（小さいほど密集して見える）
 *  - `ENTRY_DURATION_SEC`: 1 枚あたりの opacity 0→1 トランジション時間
 */
const ENTRY_STAGGER_SEC = 0.04;
const ENTRY_DURATION_SEC = 0.35;

/**
 * breakOut（Stage が position:fixed）時のジェスチャ → scrollTop 転送パラメータ。
 *  - `GESTURE_SCROLL_GAIN`: 入力（wheel / touch / 慣性）の移動量に対する送り量の
 *    倍率。1 より大きいほど少ない移動でカードが大きく動き、SP でも軽快に
 *    スワイプできる。
 *  - `MOMENTUM_FRICTION`: 指を離した後の慣性スクロールの減衰係数（毎フレーム乗算）。
 *    1 に近いほど長く滑る。
 *  - `MOMENTUM_MIN_VELOCITY`: 慣性を打ち切る速度(px/frame)。これ未満になったら
 *    最寄りカードへスナップする。
 *  - `MOMENTUM_MAX_VELOCITY`: 初速の上限(px/frame)。暴走を防ぐ。
 */
const GESTURE_SCROLL_GAIN = 1.6;
const MOMENTUM_FRICTION = 0.94;
const MOMENTUM_MIN_VELOCITY = 0.4;
const MOMENTUM_MAX_VELOCITY = 60;
/**
 * 慣性の基準フレーム時間(ms)。速度・減衰は「60fps の 1 フレームあたり」を
 * 基準単位とし、rAF の実測経過時間で按分する（120Hz の iPad でも 60Hz の
 * PC でも同じ滑り量・減衰カーブになる）。
 */
const MOMENTUM_FRAME_MS = 1000 / 60;
/**
 * 指を離す直前にこの時間(ms)以上動きが止まっていたら慣性を発生させない
 * （「ドラッグして押さえて止めてから離す」操作でカードが滑り出さないように）。
 */
const TOUCH_MOMENTUM_HOLD_CANCEL_MS = 100;
/** スワイプ後の互換 click をカード選択として扱わないための距離・時間。 */
const TOUCH_CLICK_SUPPRESS_DISTANCE_PX = 8;
const TOUCH_CLICK_SUPPRESS_MS = 500;

/**
 * horizontal レイアウトで、ループ 1 周分の横幅（N × pitch）に最低限確保する
 * 「Stage 幅 + カード 1 枚 + マージン」の余裕(px)。k = ±N/2 のラップ点で
 * カードが画面外に出た状態でジャンプするようにし、視覚ジャンプを隠す。
 */
const HORIZONTAL_LOOP_MARGIN_PX = 64;

/**
 * velocityZoom（スクロール速度連動ズームアウト）のパラメータ。
 *  - DEFAULT_VELOCITY_ZOOM_MIN_SCALE: 最大ズームアウト時の倍率。
 *  - DEFAULT_VELOCITY_ZOOM_MAX_VELOCITY_PX_MS: この速度(px/ms)で minScale に到達する。
 *  - VELOCITY_ZOOM_RESPONSE_EXPONENT: 速度 → ズーム強度カーブの指数（1 未満で凹型）。
 *    0.5（平方根）だと maxVelocity の 1/4 の速度で強度 50% に達し、
 *    軽いスクロールでもはっきり引きの絵になる。フル到達には maxVelocity が必要。
 *  - VELOCITY_ZOOM_SAMPLE_WEIGHT: scroll イベントごとの速度 EMA の新値重み。
 *  - VELOCITY_ZOOM_DECAY_PER_FRAME: scroll イベントが途絶えた後の毎フレーム減衰係数
 *    （1 に近いほどズームが長く残る）。
 *  - VELOCITY_ZOOM_EASE: 現在倍率 → 目標倍率 の毎フレーム追従係数。
 *  - VELOCITY_ZOOM_MAX_EVENT_DELTA_PX: 1 イベントでこれを超える移動量は
 *    ステルスジャンプ（ループ巻き戻し / 初回前進）とみなし速度計測から除外する。
 */
const DEFAULT_VELOCITY_ZOOM_MIN_SCALE = 0.5;
const DEFAULT_VELOCITY_ZOOM_MAX_VELOCITY_PX_MS = 2.5;
const VELOCITY_ZOOM_RESPONSE_EXPONENT = 0.5;
const VELOCITY_ZOOM_SAMPLE_WEIGHT = 0.35;
const VELOCITY_ZOOM_DECAY_PER_FRAME = 0.9;
const VELOCITY_ZOOM_EASE = 0.18;
const VELOCITY_ZOOM_MAX_EVENT_DELTA_PX = 600;
/**
 * ナビゲーション起点（左右キー / カードクリック）の smooth scroll 中、velocityZoom の
 * 速度計測を止める時限(ms)。1 ステップ操作のたびにズームがバウンスすると
 * 目が疲れるため、「自由スクロール」だけにズームを効かせる。ブラウザの
 * smooth scroll は 1 ステップ ~500ms 程度・wrap 時の複数 step でも 1s 以内に
 * 収まる想定。キー押しっぱなしでは押下のたびに再アームされる。
 */
const VELOCITY_ZOOM_NAV_SUPPRESS_MS = 1000;
/**
 * arc レイアウトのズームアウトで見せてよいホイール最深角(deg)。
 * これより深く引くと θ が 180° に近い「ホイール裏側」のほぼ逆さのカードが
 * Stage 下端から覗いてしまう。135° はカードが横倒しを超えて傾き始める手前。
 */
const ARC_VELOCITY_ZOOM_MAX_REVEAL_DEG = 135;

/**
 * スタックプレビュー（Shift ホールドで全カードを中央へ集約）のパラメータ。
 *  - STACK_TWEEN_IN_SEC / STACK_TWEEN_OUT_SEC: 集約 / 復帰トゥイーンの時間(s)。
 *    集約側は「さっと」立ち上がる power3.out、復帰側は滑らかな power2.inOut。
 *  - STACK_STAGGER_SPAN: 中央から遠いカードほど遅れて動く「波」の幅。
 *    全体進行 0..1 のうち、最遠カードの開始がこの割合ぶん遅れる
 *    （アニメーション過程で枚数の体感を出すため）。
 *  - STACK_SCALE: スタック時のカード縮小率（scaleFan にさらに乗算）。
 *    わずかに縮めて山全体を 1 画面に収める。
 *  - STACK_FAN_PIVOT_RATIO: 扇の回転半径 = カード高さ(scaleFan 適用後) × 係数。
 *    1 強でカード下端のすぐ外側をピボットに持つ「手札」の見た目になる。
 *
 * 重なり間隔（ピッチ）と広がり間隔（扇の開き角）は固定値ではなく、
 * ビューポート実寸（Stage 幅 / viewport 下端までの余白）から毎回逆算する:
 *  - horizontal: 山全体が「Stage 幅 − 縮小後カード 1 枚 − 左右 inset」を
 *    ちょうど使い切るピッチ。少数カード時の上限は
 *    STACK_MAX_PITCH_CARD_RATIO（縮小後カード幅比）で、隣接カードが
 *    必ず重なる = スタックに見えることを保証する。
 *  - arc（扇）: 端カードが Stage の左右端・viewport 下端からはみ出さない
 *    最大半開き角を computeStackFanHalfSpanDeg で逆算し、その範囲いっぱいに
 *    開く。少数カード時のピッチ上限は「ピボットから見たカードの角幅 ×
 *    STACK_FAN_MAX_PITCH_CARD_RATIO」で、こちらも必ず重なりを保つ。
 */
const STACK_TWEEN_IN_SEC = 0.32;
const STACK_TWEEN_OUT_SEC = 0.24;
const STACK_STAGGER_SPAN = 0.35;
const STACK_SCALE = 0.72;
const STACK_FAN_PIVOT_RATIO = 1.15;
/**
 * horizontal スタックの隣接ずらし量の上限 = 縮小後カード幅 × この比率。
 * 0.5 なら隣接カードが常に 50% 以上重なる（広い画面でもバラけない）。
 */
const STACK_MAX_PITCH_CARD_RATIO = 0.5;
/**
 * arc スタック（扇）の隣接角度ピッチの上限 = カードの角幅 × この比率。
 * 0.6 なら隣接カードが常に 40% 以上重なる。
 */
const STACK_FAN_MAX_PITCH_CARD_RATIO = 0.6;
/**
 * 扇の半開き角のハードリミット(deg)。これ以上開くと端カードの回転が
 * 横倒しに近づき「手札」の見た目が崩れるため、viewport が広くても開かない。
 */
const STACK_FAN_MAX_HALF_SPAN_DEG = 65;
/** 扇の半開き角の下限(deg)。極端に狭い viewport でも完全な一直線にはしない。 */
const STACK_FAN_MIN_HALF_SPAN_DEG = 4;
/** 件数ラベルと山（縮小後カード下端）との縦の隙間(px)。 */
const STACK_LABEL_GAP_PX = 24;
/**
 * スタックプレビュー中の「選択カードのせり上がり量」= 縮小後カード高さ × この比率。
 * 現在選択（現在中央、k ≈ 0）のカードだけをカード軸に沿ってわずかにせり上げ、
 * 「いま選ばれている 1 枚」を示す。上げすぎると扇のシルエットが崩れて見えるため、
 * 「浮いている」と分かる最小限（選択表示の主役は青ボーダー + 最前面 z-index）に
 * 抑える。
 */
const STACK_LIFT_CARD_RATIO = 0.06;

// ===== Helpers =====

/**
 * arc レイアウトの velocityZoom 実効下限倍率（zoom floor）。
 *
 * ホイール上のカード中心は Stage ローカルで
 *   y(θ, z) = cardCenterY + z * R * (1 - cosθ)
 * （ZoomLayer の transform-origin が cardCenterY のため zoom は R 項だけを縮める）。
 * カードの bbox 上端は y - z * halfDiag より下がらないので、角度 θ のカードが
 * 可視下端 visibleBottomPx より完全に下へ隠れる条件は
 *   cardCenterY + z * R * (1 - cosθ) - z * halfDiag >= visibleBottomPx
 * これを θ = ARC_VELOCITY_ZOOM_MAX_REVEAL_DEG（これより深い = 裏側に近い
 * カードは見せない）で解くと、許容される最小の z は
 *   z >= (visibleBottomPx - cardCenterY) / (R * (1 - cos maxReveal) - halfDiag)
 * になる（y は θ について単調増加なので、maxReveal で隠れればそれより深い
 * カードも必ず隠れる）。
 */
export const computeArcVelocityZoomFloor = (args: {
  /** カードが見えなくなる可視下端（Stage ローカル y, px）。 */
  visibleBottomPx: number;
  /** アクティブカード中心の Stage 内 y（= ZoomLayer の transform-origin）。 */
  cardCenterY: number;
  /** Phase 2（回転中）のホイール半径 rotationRTarget(px)。 */
  wheelRadiusPx: number;
  /** scaleFan 適用後のカード半対角(px)。回転を含む bbox 半高の上限。 */
  cardHalfDiagPx: number;
}): number => {
  const maxRevealRad = (ARC_VELOCITY_ZOOM_MAX_REVEAL_DEG * Math.PI) / 180;
  const hideDistancePerZoom =
    args.wheelRadiusPx * (1 - Math.cos(maxRevealRad)) - args.cardHalfDiagPx;
  if (hideDistancePerZoom <= 0) return 1;
  const roomBelowCenter = args.visibleBottomPx - args.cardCenterY;
  if (roomBelowCenter <= 0) return 1;
  return Math.min(1, roomBelowCenter / hideDistancePerZoom);
};

/**
 * horizontal スタック時の隣接カードずらし量(px)。
 * 山全体（(count - 1) × pitch）が budgetPx を超えないよう、枚数が多いほど
 * 自動で詰める。枚数が少ないときは maxPitchPx のゆったりした重なりになる。
 */
export const computeStackPitchPx = (args: {
  count: number;
  budgetPx: number;
  maxPitchPx: number;
}): number => {
  if (args.count <= 1) return args.maxPitchPx;
  return Math.min(
    args.maxPitchPx,
    Math.max(0, args.budgetPx) / (args.count - 1),
  );
};

/**
 * arc スタック（扇）時の隣接カード角度ピッチ(deg)。
 * 扇全体（(count - 1) × pitch）が maxSpanDeg を超えないよう、枚数が多いほど
 * 自動で詰める。
 */
export const computeStackFanPitchDeg = (args: {
  count: number;
  maxSpanDeg: number;
  maxPitchDeg: number;
}): number => {
  if (args.count <= 1) return args.maxPitchDeg;
  return Math.min(args.maxPitchDeg, args.maxSpanDeg / (args.count - 1));
};

/**
 * arc スタック（扇）の「viewport にちょうど収まる」半開き角(deg)を逆算する。
 *
 * 端カード（角度 θ）の中心はピボット半径 R の円弧上にあり、
 *   水平移動量 = R sinθ、下方向の沈み込み = R (1 - cosθ)
 * で単調増加する。カードの bbox は回転を含めても半対角以内なので、
 *  - 水平: R sinθ <= maxReachXPx（Stage 半幅 − カード半対角 − inset）
 *  - 垂直: R (1 - cosθ) <= maxDropYPx（viewport 下端までの余白 − 半対角 − inset）
 * の両方を満たす最大の θ を返す。広い画面ほど大きく開き、狭い画面では
 * 自動的に閉じてカードがはみ出さない。
 * 見た目の破綻防止のため [STACK_FAN_MIN_HALF_SPAN_DEG,
 * STACK_FAN_MAX_HALF_SPAN_DEG] にクランプする。
 */
export const computeStackFanHalfSpanDeg = (args: {
  /** 扇のピボット半径(px)。 */
  pivotRadiusPx: number;
  /** 端カード中心が水平方向へ動ける最大距離(px)。 */
  maxReachXPx: number;
  /** 端カード中心が下方向へ沈み込める最大距離(px)。 */
  maxDropYPx: number;
}): number => {
  const R = args.pivotRadiusPx;
  if (R <= 0) return STACK_FAN_MIN_HALF_SPAN_DEG;
  const sinLimit = Math.min(1, Math.max(0, args.maxReachXPx) / R);
  const thetaXDeg = (Math.asin(sinLimit) * 180) / Math.PI;
  const cosLimit = Math.min(
    1,
    Math.max(-1, 1 - Math.max(0, args.maxDropYPx) / R),
  );
  const thetaYDeg = (Math.acos(cosLimit) * 180) / Math.PI;
  return Math.min(
    STACK_FAN_MAX_HALF_SPAN_DEG,
    Math.max(STACK_FAN_MIN_HALF_SPAN_DEG, Math.min(thetaXDeg, thetaYDeg)),
  );
};

/**
 * スタックプレビューを発動してよい Shift 押下か。
 * 修飾キー併用（⌘⇧ 系ショートカット等）・キーリピート・入力欄フォーカス中は
 * 発動しない。構造的部分型で受けてテストしやすくしている。
 */
export const shouldEnterStackPreview = (
  e: Pick<
    KeyboardEvent,
    "key" | "repeat" | "metaKey" | "ctrlKey" | "altKey" | "target"
  >,
): boolean =>
  e.key === "Shift" &&
  !e.repeat &&
  !e.metaKey &&
  !e.ctrlKey &&
  !e.altKey &&
  !isTypingTarget(e.target);

/**
 * このホイールの「外側」にモーダルが開いているか。開いていればグローバルな
 * キーボード操作（← / → / Enter / Shift ホールド）を止め、モーダル側の操作を優先する。
 *
 * 単純な `document.querySelector('[role="dialog"]')` では、ホイール自体が
 * `role="dialog"`（オファー作成オーバーレイ等）の内側に置かれているケースまで
 * 巻き込んで止めてしまう。Stage を内包するダイアログは「自分の居場所」なので
 * 除外し、Stage を含まないダイアログ（重ねて開いた Modal）だけを阻害要因とみなす。
 */
const hasBlockingDialog = (stageEl: HTMLElement | null): boolean => {
  const dialogs = document.querySelectorAll('[role="dialog"]');
  for (const dialog of dialogs) {
    if (!stageEl || !dialog.contains(stageEl)) return true;
  }
  return false;
};

/**
 * breakOut 時に Stage を「LeftColumn の右端 + insetPx」から「viewport 右 - insetPx」までに
 * 広げるための水平範囲を求める。
 */
const computeBreakoutHorizontalBounds = (
  anchorEl: HTMLElement,
  insetPx: number,
): { left: number; width: number } => {
  const viewportWidth = window.innerWidth;
  let stageLeft = insetPx;
  const layoutWrapper = anchorEl.closest(".dm-two-column-wrapper");
  if (layoutWrapper) {
    const containerEl = layoutWrapper.firstElementChild;
    const leftColumnEl = containerEl?.firstElementChild;
    if (leftColumnEl) {
      const leftRect = leftColumnEl.getBoundingClientRect();
      stageLeft = Math.max(insetPx, leftRect.right + insetPx);
    }
  }
  const stageWidth = Math.max(100, viewportWidth - stageLeft - insetPx);
  return { left: stageLeft, width: stageWidth };
};

// ===== Component =====

/**
 * 「全周 360° の同心円 → スクロールで緩やかな扇形に展開 → 続くスクロールでホイール回転」
 * までを **スクロール操作のみで完結** させる単一選択コンポーネント。
 *
 * スクロール量の解釈:
 *  - Phase 0 (scrollDelta <= 0): 円モード（progress=0）。`useCircleInitial=true` のとき
 *    円中心が viewport 中央に固定される。
 *  - Phase 1 (0 < scrollDelta < morphDistance): 円 → 扇形 morph（progress 0→1）。
 *  - Phase 2 (scrollDelta >= morphDistance): 扇形のまま wheel が回転。R は
 *    `effectiveScrollerHeight * ROTATION_R_VIEWPORT_RATIO` まで広がり、θ は中央線形
 *    （`BASE_GAP_RATIO`）+ 外側多項式（`FAN_DISTRIBUTION_POWER`）の累積関数で算出。
 *    可視範囲のカードは中央密集、端は奥にフェード、ループ境界（k=±N/2）は ±180° で
 *    閉じる。
 *  - `INFINITE_SCROLL_LOOPS` 周ぶんスクロール領域を確保。`LOOP_INITIAL_ADVANCE_LOOPS`
 *    周ぶん初回ステルス前進し、`LOOP_ANCHOR_LOOPS` 周を超えたらステルス巻き戻し。
 *    両者でループ中央付近を維持し、左右キー操作が前後対称に効く。
 *
 * 操作系:
 *  - スクロール / キーボード ← / →: scroll listener が `progress` と `fractionalCenter`
 *    を導出し、視覚中央のカードを `activeIndex` / `value` に同期する（=「中央のカードが
 *    常に選択される」UX）。`scrollToCardIndex` のロック中（クリック起動の smooth scroll）
 *    は中間値で揺れないよう sync をスキップする。
 *  - クリック: `scrollToCardIndex` が **現在の周回上で最短距離** の cardIndex 位置まで
 *    smooth scroll する（何周もスクロールした後でも巻き戻しが起きない）。
 *  - キーボード ← / →: `advanceScrollByOneStep` で 1 step ぶん smooth scroll。
 *    activeIndex 更新は scroll listener 任せ（押しっぱなしでも視覚中央 = 選択を維持）。
 *  - キーボード Space / Enter: 中央のカードを onChange で確定。Enter はさらに
 *    `onConfirm`（決定 = 遷移などの主アクション）を発火する。← / → の smooth scroll が
 *    まだ飛んでいる最中は、追従前の古いカードではなく着地先を確定する
 *    （`pendingStepTargetRef` / {@link commitActiveItem}）。
 *  - ダブルクリック: Enter と同じ「決定」。両者は常に同じ扱いにする。
 *
 * カードはいずれの状態でも opacity=1（半透明にならない）。重なり時は θ=0 を最前面に
 * する z-index 制御で「中央のカードが手前」になるようスタックする。
 *
 * カード数が `circleThreshold` 未満のときは、最初から `fanSpanDeg` の "扇形" で開始する
 *（円だと隙間が大きく見栄えがしないため）。
 */
function RadioCardGroup<T extends RadioCardGroupItem>(
  props: RadioCardGroupProps<T>,
): React.ReactElement {
  const {
    items,
    value,
    onChange,
    onConfirm,
    onDeselect,
    renderCard,
    cardSize,
    scroller,
    fanRadius: fanRadiusProp,
    fanSpanDeg = DEFAULT_FAN_SPAN_DEG,
    scaleFan = DEFAULT_SCALE_FAN,
    scaleCircle = DEFAULT_SCALE_CIRCLE,
    circleThreshold = DEFAULT_CIRCLE_THRESHOLD,
    morphDistance = DEFAULT_MORPH_DISTANCE,
    navStepPx = DEFAULT_NAV_STEP_PX,
    endBufferPx = DEFAULT_END_BUFFER_PX,
    breakOut = false,
    breakOutInsetPx = BREAKOUT_HORIZONTAL_INSET,
    verticalCenterY,
    layout = "arc",
    velocityZoom,
    activeCardScale = 1,
    cardScrollHandoff = false,
    gestureSurfaceKey,
    onSettleChange,
    stackPreview,
    onStackChange,
    advanceStepRef,
    globalKeyboardNav = false,
    disabled = false,
    className,
    ariaLabel,
  } = props;

  const N = items.length;
  /** 完全横スクロール（直線配置）レイアウトか。 */
  const isHorizontal = layout === "horizontal";
  /**
   * カードが 1 枚しかないときはホイール演出を止め、静的な固定表示にする。
   * （スクロールで唯一のカードが弧上を回って画面外へ出ると、状況が
   * 分からず混乱を生むため。ScrollSpacer も確保しない。）
   */
  const isStaticSingle = N === 1;

  // ── prefers-reduced-motion 検出 ──────────────────────
  const [reducedMotion, setReducedMotion] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  /**
   * breakOut 演出を実際に有効化するか。`reducedMotion` 中はスクロール演出ごと
   * 止めるので breakOut も無効化し、Stage は通常の sticky で振る舞う。
   * useLayoutEffect / 描画の双方で参照するためコンポーネント本体で 1 度だけ導出する。
   */
  const effectiveBreakOut = breakOut && !reducedMotion;

  // ── velocityZoom（スクロール速度連動ズームアウト） ──────────
  // 親が inline オブジェクトで渡しても effect が毎レンダー再実行されないよう、
  // ここでプリミティブに落としてから依存に使う。
  const velocityZoomEnabled =
    velocityZoom !== undefined && !reducedMotion && N > 1;
  const velocityZoomMinScale =
    velocityZoom?.minScale ?? DEFAULT_VELOCITY_ZOOM_MIN_SCALE;
  const velocityZoomMaxVelocity =
    velocityZoom?.maxVelocity ?? DEFAULT_VELOCITY_ZOOM_MAX_VELOCITY_PX_MS;

  // ── activeIndex（選択候補。aria-checked と Enter 確定対象） ──
  const [activeIndex, setActiveIndex] = useState<number>(() => {
    if (value === undefined) return 0;
    const i = items.findIndex((it) => it.id === value);
    return i >= 0 ? i : 0;
  });

  useEffect(() => {
    if (value === undefined) return;
    const i = items.findIndex((it) => it.id === value);
    if (i >= 0 && i !== activeIndex) {
      setActiveIndex(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items]);

  useEffect(() => {
    if (N === 0) return;
    if (activeIndex >= N) {
      setActiveIndex(N - 1);
    }
  }, [N, activeIndex]);

  // ── 数学パラメータ ─────────────────────────────────
  /** 円 / 扇 どちらで初期表示するか（カード数で決まる）。horizontal は常に扇なし。 */
  const useCircleInitial = !isHorizontal && N >= circleThreshold;
  /**
   * カードが少ない（5 枚未満）ときは円形が綺麗に並ばないため、初期の
   * 円→扇モーフィングや入場アニメーションを省略し、最初から扇形 (progress=1) で
   * 即スワイプできるようにする。horizontal レイアウトはモーフ演出自体が無いため
   * 常にスキップする。
   */
  const skipIntro = isHorizontal || (N > 0 && N < SMALL_CARD_SKIP_INTRO_MAX);
  /** 初期配置の中心角(deg)。 */
  const initialSpanDeg = useCircleInitial ? 360 : fanSpanDeg;
  /**
   * 着地時（Phase 1 終端）のカード間ピッチ(deg)。
   *  - 円モード（N >= circleThreshold）: 360/N で全周等間隔。
   *  - 少数カード扇モード: fanSpanDeg/N。
   *
   * Phase 2（rotation 中）は `computeTargets` 内で `effectivePitchDeg` として動的に
   * 縮まる。無限スクロール整合は `activeIndex = round(fractionalCenter) mod N` で
   * 担保しているため、effectivePitch が動的でも活性 ID は常に正しく循環する。
   */
  const pitchDeg = N > 0 ? initialSpanDeg / N : 0;

  const [measuredStageWidth, setMeasuredStageWidth] = useState<number>(0);
  /**
   * scroller（OverlayBody 等、`data-offer-overlay-scroll` で解決される要素）の実効高さ。
   * GlobalHeader / ProjectBanner / BottomAppBar 等を引いた "実際にカードが描画できる
   * 縦領域" として扱う。円モードで「円直径 + カード高さ」が収まらないときの shrink 判定、
   * Phase 2 の R 目標値（viewport の N 倍）、breakOut 時の縦中央寄せ計算に使う。
   *
   * scroller が未解決の場合は `window.innerHeight` を fallback として使う。
   * 初期値は SSR セーフのため 0、`useLayoutEffect` で実値に同期する（観測は Todo 1）。
   */
  const [effectiveScrollerHeight, setEffectiveScrollerHeight] = useState<number>(
    () => (typeof window === "undefined" ? 0 : window.innerHeight),
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const wrapperEl = wrapperRef.current;
    const resolved: Element | Window =
      scroller ??
      wrapperEl?.closest("[data-offer-overlay-scroll]") ??
      window;

    const update = () => {
      if (resolved === window) {
        setEffectiveScrollerHeight(window.innerHeight);
      } else {
        setEffectiveScrollerHeight((resolved as Element).clientHeight);
      }
    };
    update();

    if (resolved === window) {
      window.addEventListener("resize", update, { passive: true });
      return () => window.removeEventListener("resize", update);
    }
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(resolved as Element);
    return () => ro.disconnect();
  }, [scroller]);

  /**
   * 扇形モード（progress=1）でのカード "見える側" の半幅(deg)。
   * `pitchDeg × ((FAN_VISIBLE_COUNT_FOR_RADIUS - 1) / 2)` で
   * 「中央 ± k 枚」が見える範囲を意味する。
   */
  const fanVisibleHalfSpanDeg = useMemo(() => {
    const halfCount = (FAN_VISIBLE_COUNT_FOR_RADIUS - 1) / 2;
    return pitchDeg * halfCount;
  }, [pitchDeg]);

  /**
   * 扇形モード（progress=1）の半径。
   *
   * 「中央 ± `fanVisibleHalfSpanDeg` 度に並んだ端カードが Stage 横幅にちょうど
   * 収まる」という制約から逆算する。FAN_VISIBLE_COUNT_FOR_RADIUS を小さくするほど
   * visibleHalfSpan も小さくなり、R が大きくなって弧がなだらかになる。
   *
   * これより遠い角度のカードは自然に画面外へ出るので、
   * 「見えないカードはそこで誤魔化される」状態になる。
   */
  const fanRadius = useMemo<number>(() => {
    if (fanRadiusProp !== undefined) return fanRadiusProp;
    if (measuredStageWidth <= 0) return FALLBACK_RADIUS;
    const halfSpanRad = (fanVisibleHalfSpanDeg * Math.PI) / 180;
    const sinHalfSpan = Math.sin(halfSpanRad);
    const cosHalfSpan = Math.cos(halfSpanRad);
    if (sinHalfSpan <= 0) return FALLBACK_RADIUS;
    const scaledW = cardSize.width * scaleFan;
    const scaledH = cardSize.height * scaleFan;
    const rotatedBBoxW =
      Math.abs(scaledW * cosHalfSpan) + Math.abs(scaledH * sinHalfSpan);
    const maxCardX =
      measuredStageWidth / 2 - rotatedBBoxW / 2 - STAGE_EDGE_INSET;
    if (maxCardX <= 0) return MIN_AUTO_RADIUS;
    return Math.max(MIN_AUTO_RADIUS, maxCardX / sinHalfSpan);
  }, [
    fanRadiusProp,
    measuredStageWidth,
    fanVisibleHalfSpanDeg,
    cardSize.width,
    cardSize.height,
    scaleFan,
  ]);

  /**
   * 円モード（progress=0）の半径と effective scale。
   *
   *  1. 素朴な scaleCircle で "自然半径"（N 枚が CIRCLE_CARD_GAP_FACTOR の間隔で並ぶ）
   *     と "Stage 横幅キャップ" の min から rawR を導出。
   *  2. 円が viewport 高さに収まらない場合（`2*R + cardH*scaleCircle + 2*STAGE_PADDING`
   *     > viewportH）、`shrink = viewportH / required` で R とカードを同じ比率で縮める。
   *     → これによりカード縮小率（= 1 - effectiveScaleCircle）が動的に上がり、
   *       ビューポート内に円全体が収まる（要件: 「周りカードを描く余裕がない場合は、
   *       カード縮小率を上げる」）。
   *  3. 縮小後の `effectiveScaleCircle = scaleCircle * shrink` を `computeTargets` /
   *     `stageHeight` 計算でも使う。
   *
   * 5 枚以下の扇モード（useCircleInitial=false）では `circleRadius` は内部利用されるが
   * progress=0 で `lerp(circleRadius, fanRadius, 0)` = circleRadius が active 時に使われる
   * ことはほぼない（`useCircleInitial=false` のときも同じ値で動く）。
   */
  const { circleRadius, effectiveScaleCircle } = useMemo<{
    circleRadius: number;
    effectiveScaleCircle: number;
  }>(() => {
    if (N <= 0) {
      return {
        circleRadius: FALLBACK_RADIUS / 2,
        effectiveScaleCircle: scaleCircle,
      };
    }
    // 1. raw R を scaleCircle ベースで算出
    const cardSlotW = cardSize.width * scaleCircle * CIRCLE_CARD_GAP_FACTOR;
    const naturalR = (N * cardSlotW) / (2 * Math.PI);
    const halfCardW = (cardSize.width * scaleCircle) / 2;
    const widthCapR =
      measuredStageWidth > 0
        ? measuredStageWidth / 2 - halfCardW - STAGE_EDGE_INSET
        : FALLBACK_RADIUS;
    const rawR = Math.max(
      MIN_AUTO_RADIUS / 2,
      Math.min(naturalR, widthCapR),
    );

    // 2. scroller の実効高さ収まり判定。円モードでないとき（5 枚以下など）はスキップ。
    if (!useCircleInitial || effectiveScrollerHeight <= 0) {
      return { circleRadius: rawR, effectiveScaleCircle: scaleCircle };
    }
    const requiredH =
      2 * rawR + cardSize.height * scaleCircle + 2 * STAGE_PADDING;
    if (requiredH <= effectiveScrollerHeight) {
      return { circleRadius: rawR, effectiveScaleCircle: scaleCircle };
    }
    const shrink = effectiveScrollerHeight / requiredH;
    return {
      circleRadius: Math.max(MIN_AUTO_RADIUS / 2, rawR * shrink),
      effectiveScaleCircle: scaleCircle * shrink,
    };
  }, [
    N,
    cardSize.width,
    cardSize.height,
    scaleCircle,
    measuredStageWidth,
    effectiveScrollerHeight,
    useCircleInitial,
  ]);

  /**
   * 上端カード（θ=0、active 位置）の Stage 内 y 座標。
   *  - Slot の CSS `top` として描画位置決めに使う
   *  - 計算上の wheel 中心 cy = cardCenterY + R を導く基準にもなる
   *
   * fan モードでのカードサイズ（最も大きい状態）を基準に padding を取り、
   * 円モード / 回転中でも上端のカードが Stage 上端を超えない位置に置く。
   */
  const cardCenterY = STAGE_PADDING + (cardSize.height * scaleFan) / 2;

  /**
   * Phase 2（rotation 中）で目指す半径(px)。
   * scroller の実効高さが取得できていればその `ROTATION_R_VIEWPORT_RATIO` 倍、
   * できていなければ fallback として `fanRadius * ROTATION_R_MULTIPLIER`。
   * fanRadius を下回らないようクリップする。
   * `computeTargets` の R 補間先と `stageHeight` の下端カード見積もりで共通利用する。
   */
  const rotationRTarget = useMemo<number>(() => {
    if (effectiveScrollerHeight > 0) {
      return Math.max(
        fanRadius,
        effectiveScrollerHeight * ROTATION_R_VIEWPORT_RATIO,
      );
    }
    return fanRadius * ROTATION_R_MULTIPLIER;
  }, [fanRadius, effectiveScrollerHeight]);

  /**
   * horizontal レイアウトの隣接カード中心間距離(px)。
   * 基本は「カード幅 × BASE_GAP_RATIO」（カード同士が少し空く間隔）だが、
   * ループ 1 周（N × pitch）が「Stage 幅 + カード幅 + マージン」を下回ると
   * k = ±N/2 のラップ点のジャンプが画面内で見えてしまうため、下限を確保する。
   */
  const horizontalPitchPx = useMemo<number>(() => {
    const scaledW = cardSize.width * scaleFan;
    const basePitch = scaledW * BASE_GAP_RATIO;
    if (N <= 1) return basePitch;
    const stageW =
      measuredStageWidth > 0 ? measuredStageWidth : FALLBACK_RADIUS * 2;
    const minLoopSpan = stageW + scaledW + HORIZONTAL_LOOP_MARGIN_PX;
    return Math.max(basePitch, minLoopSpan / N);
  }, [N, cardSize.width, scaleFan, measuredStageWidth]);

  // ── スタックプレビュー（Shift ホールド）の導出値 ──────────
  /** スタックプレビューを実際に有効化するか（オプトイン + 複数カード + 有効時のみ）。 */
  const stackPreviewEnabled = stackPreview !== undefined && N > 1 && !disabled;
  /** スタック時のカードの最終 transform scale。 */
  const stackCardScale = scaleFan * STACK_SCALE;
  /** スタック時の縮小後カード実寸(px)。ピッチ算出と件数ラベル位置で共用。 */
  const stackCardWidthPx = cardSize.width * stackCardScale;
  const stackCardHeightPx = cardSize.height * stackCardScale;
  /** 扇スタックのピボット半径(px)。ピッチ算出と computeTargets の集約先で共用。 */
  const stackFanPivotRadiusPx =
    cardSize.height * scaleFan * STACK_FAN_PIVOT_RATIO;
  /**
   * horizontal スタックの隣接カードずらし量(px)。viewport 実寸から逆算する:
   * 山全体（(N-1) × pitch）が「Stage 幅 − 縮小後カード 1 枚 − 左右 inset」を
   * ちょうど使い切る。少数カード時は STACK_MAX_PITCH_CARD_RATIO（カード幅比）
   * で頭打ちにして、隣接カードが必ず重なる = スタックに見えることを保証する。
   */
  const stackPitchPx = useMemo<number>(() => {
    const stageW =
      measuredStageWidth > 0 ? measuredStageWidth : FALLBACK_RADIUS * 2;
    const budgetPx = Math.max(
      0,
      stageW - stackCardWidthPx - STAGE_EDGE_INSET * 2,
    );
    return computeStackPitchPx({
      count: N,
      budgetPx,
      maxPitchPx: stackCardWidthPx * STACK_MAX_PITCH_CARD_RATIO,
    });
  }, [N, stackCardWidthPx, measuredStageWidth]);
  /**
   * arc スタック（扇）の隣接カード角度ピッチ(deg)。viewport 実寸から逆算する:
   * 端カードが Stage の左右端・viewport 下端からはみ出さない最大の開き角
   * （computeStackFanHalfSpanDeg）いっぱいに (N-1) 枚ぶんを均等配置する。
   * 少数カード時は「ピボットから見たカードの角幅 ×
   * STACK_FAN_MAX_PITCH_CARD_RATIO」で頭打ちにして、隣接カードが必ず重なる
   * 手札の見た目を保つ。
   */
  const stackFanPitchDeg = useMemo<number>(() => {
    // カード未測定（サイズ 0）の間はピッチ 0（カードの可視サイズも 0）。
    if (stackFanPivotRadiusPx <= 0) return 0;
    const cardHalfDiagPx =
      Math.hypot(stackCardWidthPx, stackCardHeightPx) / 2;
    const stageW =
      measuredStageWidth > 0 ? measuredStageWidth : FALLBACK_RADIUS * 2;
    // 中央カード中心の viewport Y。breakOut ページは verticalCenterY を渡す。
    // 未指定（非 breakOut 等）は viewport 縦中央とみなす。
    const viewportH =
      typeof window !== "undefined"
        ? window.innerHeight
        : effectiveScrollerHeight;
    const centerY = verticalCenterY ?? viewportH / 2;
    const halfSpanDeg = computeStackFanHalfSpanDeg({
      pivotRadiusPx: stackFanPivotRadiusPx,
      maxReachXPx: stageW / 2 - cardHalfDiagPx - STAGE_EDGE_INSET,
      maxDropYPx: viewportH - centerY - cardHalfDiagPx - STAGE_EDGE_INSET,
    });
    // ピボットから見たカードの角幅(deg)。この比率以下のピッチなら必ず重なる。
    const cardAngularWidthDeg =
      (2 *
        Math.asin(Math.min(1, stackCardWidthPx / (2 * stackFanPivotRadiusPx))) *
        180) /
      Math.PI;
    return computeStackFanPitchDeg({
      count: N,
      maxSpanDeg: halfSpanDeg * 2,
      maxPitchDeg: cardAngularWidthDeg * STACK_FAN_MAX_PITCH_CARD_RATIO,
    });
  }, [
    N,
    stackCardWidthPx,
    stackCardHeightPx,
    stackFanPivotRadiusPx,
    measuredStageWidth,
    effectiveScrollerHeight,
    verticalCenterY,
  ]);
  /** 件数ラベルに出す枚数。 */
  const stackLabelCount = stackPreview?.count ?? N;
  /** 件数ラベルの縦位置: 縮小後の中央カード下端 + 隙間。 */
  const stackLabelTop = cardCenterY + stackCardHeightPx / 2 + STACK_LABEL_GAP_PX;

  const stageHeight = useMemo(() => {
    // horizontal レイアウトは縦に膨らまないため、カード 1 枚分 + padding のみ。
    if (isHorizontal) {
      return Math.ceil(
        cardCenterY + (cardSize.height * scaleFan) / 2 + STAGE_PADDING,
      );
    }

    // 円モードでは下端カード（θ=180°）の visual bottom が一番下に来る。
    //   visual bottom = cardCenterY + 2*R_circle + cardH*effectiveScaleCircle/2
    const circleExtent =
      cardCenterY +
      2 * circleRadius +
      (cardSize.height * effectiveScaleCircle) / 2 +
      STAGE_PADDING;

    // 扇モードでは "見える側" の端カード（θ=fanVisibleHalfSpanDeg）が一番下。
    // それより外側のカードは画面外なので高さ計算に入れない。
    // 回転中は R が rotationRTarget まで広がる可能性があるので、その値で計算する。
    const halfFanRad = (fanVisibleHalfSpanDeg * Math.PI) / 180;
    const fanRise = rotationRTarget * (1 - Math.cos(halfFanRad));
    const sinHalfFan = Math.abs(Math.sin(halfFanRad));
    const cosHalfFan = Math.abs(Math.cos(halfFanRad));
    const scaledW = cardSize.width * scaleFan;
    const scaledH = cardSize.height * scaleFan;
    const rotatedHalfH = (scaledW * sinHalfFan + scaledH * cosHalfFan) / 2;
    const fanExtent =
      cardCenterY + fanRise + rotatedHalfH + STAGE_PADDING;

    return Math.ceil(Math.max(circleExtent, fanExtent));
  }, [
    isHorizontal,
    cardCenterY,
    circleRadius,
    cardSize.height,
    cardSize.width,
    effectiveScaleCircle,
    scaleFan,
    fanVisibleHalfSpanDeg,
    rotationRTarget,
  ]);

  /**
   * velocityZoom の実効下限倍率。minScale=0 を渡すとここまで引ける（最大ズーム）。
   *
   * horizontal: `horizontalPitchPx` の下限確保は「scale 1 でラップ点（k = ±N/2）が
   * 画面外」を保証しているだけなので、ズームアウトで可視範囲が `stageW / z` に
   * 広がるとループ境界のジャンプが見えてしまう。ラップ点が Stage 内に入らない条件
   * `N × pitch >= stageW / z + scaledW + margin` から許容最小 z を逆算してクランプする。
   *
   * arc: θ は ±180° で連続して閉じるためループジャンプは起きないが、引きすぎると
   * ホイール裏側（ほぼ逆さのカード）が可視下端から覗く。可視下端は
   * min(Stage 下端, viewport 下端)（breakOut の Stage は viewport より縦長になる
   * ため通常 viewport 側が効く）とし、最深角を ARC_VELOCITY_ZOOM_MAX_REVEAL_DEG に
   * 抑える z でクランプする（computeArcVelocityZoomFloor）。ズームが効くのは
   * Phase 2 のみで、そのとき R は rotationRTarget に到達済み
   * （ステルス前進で fractionalCenter >= ROTATION_R_RAMP_STEPS になる）なので
   * 半径は rotationRTarget で評価してよい。
   *
   * いずれもカードが少ないときは自動的に浅くなり、多いときは深く引ける。
   */
  const velocityZoomEffectiveMinScale = useMemo<number>(() => {
    if (!velocityZoomEnabled) return 1;
    if (!isHorizontal) {
      // breakOut 時の Stage top は「アクティブカード中心が verticalCenterY
      // （未指定なら viewport 中央）に来る位置」= verticalCenterY - cardCenterY。
      // viewport 下端を Stage ローカル y に直して可視下端を求める。
      const viewportH =
        typeof window !== "undefined" ? window.innerHeight : stageHeight;
      const stageTop = (verticalCenterY ?? viewportH / 2) - cardCenterY;
      const visibleBottomPx = Math.min(stageHeight, viewportH - stageTop);
      const cardHalfDiagPx =
        Math.hypot(cardSize.width * scaleFan, cardSize.height * scaleFan) / 2;
      return Math.max(
        velocityZoomMinScale,
        computeArcVelocityZoomFloor({
          visibleBottomPx,
          cardCenterY,
          wheelRadiusPx: rotationRTarget,
          cardHalfDiagPx,
        }),
      );
    }
    const stageW =
      measuredStageWidth > 0 ? measuredStageWidth : FALLBACK_RADIUS * 2;
    const scaledW = cardSize.width * scaleFan;
    const loopSpan =
      N * horizontalPitchPx - scaledW - HORIZONTAL_LOOP_MARGIN_PX;
    if (loopSpan <= 0) return 1;
    const zoomFloor = stageW / loopSpan;
    return Math.min(1, Math.max(velocityZoomMinScale, zoomFloor));
  }, [
    velocityZoomEnabled,
    isHorizontal,
    stageHeight,
    verticalCenterY,
    cardCenterY,
    rotationRTarget,
    measuredStageWidth,
    cardSize.width,
    cardSize.height,
    scaleFan,
    N,
    horizontalPitchPx,
    velocityZoomMinScale,
  ]);

  // ── Stage が sticky な間に確保するスクロール距離 ─────────
  // 無限スクロール: N の `INFINITE_SCROLL_LOOPS` 倍ぶんを sticky 区間にする。
  // wheel は `pitchDeg = 360/N` なので、N 回 advance で 1 周。
  // INFINITE_SCROLL_LOOPS 周ぶん安定的にスクロールできる。
  // カード 1 枚のときはスクロール演出を行わないため 0（固定表示）。
  const totalPinDistance = useMemo(() => {
    if (N <= 1) return 0;
    const navTotal = Math.max(0, INFINITE_SCROLL_LOOPS * N) * navStepPx;
    return morphDistance + navTotal + endBufferPx;
  }, [N, morphDistance, navStepPx, endBufferPx]);

  // ── Refs ──────────────────────────────────────────────
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const zoomLayerRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const onConfirmRef = useRef(onConfirm);
  const onDeselectRef = useRef(onDeselect);
  const onSettleChangeRef = useRef(onSettleChange);
  const onStackChangeRef = useRef(onStackChange);
  const suppressClickUntilRef = useRef(0);
  // commitActiveItem が「中央のカード」を最新の items から引けるように ref 経由で
  // 参照する（グローバルリスナーを items 変更で張り直さないため）。
  const itemsRef = useRef(items);

  // コールバック更新だけでスクロール・タッチ購読を張り直さない。
  onChangeRef.current = onChange;
  onConfirmRef.current = onConfirm;
  onDeselectRef.current = onDeselect;
  onSettleChangeRef.current = onSettleChange;
  onStackChangeRef.current = onStackChange;
  itemsRef.current = items;

  /**
   * スタックプレビューの進行度（0 = 通常ホイール、1 = 完全スタック）。
   * GSAP がこのオブジェクトの progress を直接トゥイーンし、onUpdate の
   * applyInstant が computeTargets 経由で読む。
   */
  const stackStateRef = useRef({ progress: 0 });
  /** Shift ホールド中か（進行度とは別の論理状態。ジェスチャ抑制にも使う）。 */
  const stackActiveRef = useRef(false);
  /** 進行度トゥイーンのハンドル。再入時に kill して張り直す。 */
  const stackTweenRef = useRef<ReturnType<typeof gsap.to> | null>(null);
  /** 件数ラベル要素。opacity / visibility をトゥイーンから直接更新する。 */
  const stackLabelRef = useRef<HTMLDivElement>(null);

  /**
   * ジェスチャコントローラ（wheel / touch / 慣性を一元管理するステートマシン）。
   * breakOut モードのジェスチャ転送 effect 内で生成され、クリック / キーボード
   * ナビゲーション（`scrollToCardIndex` / `advanceScrollByOneStep`）から
   * `beginNavigation()` を伝えるために ref でも保持する。
   */
  const gestureControllerRef = useRef<WheelGestureController | null>(null);

  /**
   * 親へ通知済みの settled 値。effect 再実行でコントローラが作り直されても
   * 通知の重複 / 欠落が起きないよう、コンポーネントレベルで重複排除する。
   */
  const settledStateRef = useRef(false);
  const notifySettled = useCallback((next: boolean) => {
    if (settledStateRef.current === next) return;
    settledStateRef.current = next;
    onSettleChangeRef.current?.(next);
  }, []);

  /**
   * velocityZoom の内部状態。scroll イベント（速度サンプリング）と rAF ループ
   * （減衰 + 倍率追従）の双方から参照するため ref に持つ。
   *  - velocity: 平滑化済みスクロール速度(px/ms)
   *  - zoom: 現在の表示倍率
   *  - raf: 追従ループの rAF ハンドル（null = 停止中）
   *  - lastDelta / lastTime: 前回 scroll イベントの scrollDelta / タイムスタンプ
   */
  const velocityZoomStateRef = useRef({
    velocity: 0,
    zoom: 1,
    raf: null as number | null,
    lastDelta: null as number | null,
    lastTime: 0,
  });

  /**
   * この時刻（performance.now() 基準）まで velocityZoom の速度計測を止める。
   * ナビゲーション起点の smooth scroll でズームがバウンスしないようにするための時限。
   */
  const velocityZoomNavSuppressUntilRef = useRef(0);

  /**
   * ナビゲーション起点（左右キー / カードクリック / 停止時スナップ）の smooth
   * scroll を始める直前に呼び、velocityZoom の速度計測を一時停止する。
   * 呼ぶたびに時限が再アームされるので、キー連打中は止まり続ける。
   */
  const suppressVelocityZoomForNav = useCallback(() => {
    velocityZoomNavSuppressUntilRef.current =
      performance.now() + VELOCITY_ZOOM_NAV_SUPPRESS_MS;
  }, []);

  /** 0..1 のモーフィング進行。スクロール由来。 */
  const progressRef = useRef<number>(reducedMotion ? 1 : 0);
  /**
   * ホイール上端（θ=0、active 位置）に来ているカードの index（小数）。
   * scroll の Phase 2（morph 完了後）で連続的に進み、wheel を回転させる。
   */
  const fractionalCenterRef = useRef<number>(0);
  const activeIndexRef = useRef<number>(activeIndex);

  /** 解決済み scroller を click / keyboard から参照するための ref。 */
  const resolvedScrollerRef = useRef<Element | Window | null>(null);

  /**
   * ブラウザ smooth-scroll 中に scroll listener が "中間の round 値" で
   * activeIndex を巻き戻すのを防ぐためのロック。期限切れまで sync をスキップする。
   */
  const syncLockedUntilRef = useRef<number>(0);
  /**
   * ← / → の smooth scroll が「これから中央に来る」カードの index と、その有効期限。
   *
   * キー移動は `activeIndex` を即時更新せず scroll listener の追従に任せるため、
   * スクロール中に Enter を押すと「まだ中央から去っていない古いカード」が決定
   * されてしまう（選択が 1 枚ぶん巻き戻る）。決定時はこの着地先を優先して使う。
   * 着地するか期限切れになったらクリアされる。
   */
  const pendingStepTargetRef = useRef<{ index: number; expiresAt: number } | null>(
    null,
  );
  /**
   * Phase 2 に初回到達したあと、scrollTop を「中央周回」までステルス前進したか。
   * 一度前進したらループ巻き戻しを跨ぐまで再発火しない（= true 維持）。
   * Phase 0 / Phase 1 に戻ったら false にリセットして、再度 Phase 2 入った時に
   * もう一度中央寄せを行う。
   */
  const phase2InitialAdvancedRef = useRef<boolean>(false);

  /**
   * 入場演出（"左から円を描くように" カードを stagger 表示）が走っている間 true。
   * 期間中は `applyInstant` がスクロール由来で再呼び出しされても opacity は上書き
   * しない（GSAP の opacity tween と衝突させないため）。`playEntryAnimation` で
   * true に立て、見積もり完了時間後の setTimeout で false に戻す。
   */
  const entryActiveRef = useRef<boolean>(false);
  /**
   * 入場演出を発火すべきかの判定用に「前回 useLayoutEffect 走査時の items 参照」を
   * 覚えておく。初期マウント（null → 初回 items）とタグ切替（items 入れ替え）で
   * 発火し、stageHeight 等の他依存だけ変わった再走では発火しない。
   */
  const lastEntryItemsRef = useRef<typeof items | null>(null);
  /**
   * `playEntryAnimation` 内で `entryActiveRef` を false に戻すために予約する
   * setTimeout の ID。再発火時 / アンマウント時に既存のタイマーを解除して、
   * 古い tween 完了タイマが現役の入場演出を誤って打ち切らないようにする。
   */
  const entryActiveResetTimerRef = useRef<number | null>(null);

  // ── レイアウト計算 ────────────────────────────────────
  /**
   * 各カードの目標 transform を計算する。
   *
   *  - θ_i は rotationT で動的補間する:
   *      Phase 1 まで（rotationT=0）: 均等配置 thetaUniform = (i - fractionalCenter) * pitchDeg
   *        （pitchDeg = 円モード: 360/N、少数カード扇: fanSpanDeg/N）
   *      Phase 2 進行（rotationT→1）: 「線形 + 外側多項式」累積関数 thetaVisible:
   *        basePitchDeg = 2*asin(BASE_GAP_RATIO * wEff / (2R))
   *        thetaVisible(k) = sign(k) * [basePitchDeg*|k| + remainder*(|k|/halfN)^p]
   *        中央 (|k|=0,1) は basePitch（≈ 100% カード幅、ぴったり接する）で滑らかに線形、
   *        |k| が halfN に近づくほど多項式で急激に加速して画面奥に到達。
   *        ループ境界（k = ±N/2）で正確に ±180° に閉じるため、k ラップ点での視覚
   *        ジャンプが起きない。
   *  - 半径 R は 2 段階の lerp:
   *      1) morph: circleRadius → fanRadius (200%/50% クランプ済) （progress 0→1）
   *      2) rotation: phase1R → rotationRTarget（= max(fanRadius, scrollerH * ratio)）
   *         （fractionalCenter が動き始めると更に広がる。弧度をなだらかにする）
   *  - scale = lerp(effectiveScaleCircle, scaleFan, t)
   *  - x = R sin θ、y = R (1 - cos θ)
   *  - rotation = θ（常に tangent）
   *  - z-index: 中央（θ ≈ 0）が最前面、奥（θ ≈ ±180）が最背面
   *
   * `activeIndex = round(fractionalCenter) mod N` で活性 ID は常に正しく循環する
   * （θ_i が動的でも fractionalCenter の数値だけで決まるので無限スクロールの整合は
   * 保たれる。視覚的にも活性カードは θ=0 にあるので問題なし）。
   */
  const computeTargets = useCallback(() => {
    const progress = progressRef.current;
    const fractionalCenter = fractionalCenterRef.current;
    const t = easeInOut(progress);

    // ── スタックプレビュー（Shift ホールド）の進行度 ──
    // カードごとに |k| に応じた遅延を入れ、外側のカードが少し遅れて重なる
    // 「さっと寄る」波を作る（アニメーション過程でも枚数の体感を出すため）。
    const stackProgress = stackStateRef.current.progress;
    const staggerHalfN = Math.max(1, N / 2);
    const stackProgressFor = (absK: number): number => {
      if (stackProgress <= 0) return 0;
      const rank = Math.min(1, absK / staggerHalfN);
      const local =
        stackProgress * (1 + STACK_STAGGER_SPAN) - rank * STACK_STAGGER_SPAN;
      return easeInOut(Math.min(1, Math.max(0, local)));
    };
    // スタック配置も通常ホイールと同じく現在中央（fractionalCenter）基準。
    // スタック表示中の ← / → では山（扇）全体が 1 pitch ずつ回転し、
    // 選択カードは常に山の中央に留まる（Shift なしのホイール操作と同じ視点）。
    /** せり上がり量の最大値(px)。カードごとの実量は中央近接度 × sStack を乗じる。 */
    const stackLiftMaxPx = stackCardHeightPx * STACK_LIFT_CARD_RATIO;

    // ── horizontal レイアウト: 一直線の横並び（回転・円弧なし） ──
    // k（中心からの最短周回距離）をそのまま水平オフセットに変換する。
    // ラップ点（k = ±N/2）は horizontalPitchPx の下限確保により画面外になる。
    if (isHorizontal) {
      const halfN = N / 2;
      return Array.from({ length: N }, (_, i) => {
        const rawK = i - fractionalCenter;
        const k =
          N > 0 ? ((((rawK + halfN) % N) + N) % N) - halfN : rawK;
        const absK = Math.abs(k);
        // 中央近接度: 中央（k=0）で 1、|k| >= 1 で 0 になる easeInOut 減衰。
        // fractionalCenter 連動なのでスクロール中は隣のカードへ連続的に
        // 受け渡され、カクつかない。
        const centerEmphasis = easeInOut(Math.max(0, 1 - absK));
        // 中央カード強調（activeCardScale）: |k| < 1 の範囲だけ倍率をブースト。
        // 中央が最前面（zIndex）なので、拡大でわずかに隣へ被っても自然に重なる。
        const focusBoost = 1 + (activeCardScale - 1) * centerEmphasis;
        const wheelX = k * horizontalPitchPx;
        const wheelScale = scaleFan * focusBoost;
        // 中央（k ≈ 0）が最前面。距離に応じて奥へ。
        const wheelZIndex = 1000 - absK * 10;
        // スタックプレビュー: 横のままピッチを stackPitchPx へ詰めて重ね、
        // z-index は「右のカードが上」の積み順へ遷移する。← / → では中央
        //（k ≈ 0）を固定したまま山全体が流れ、中央のカードだけわずかに
        // 上へせり上げて選択を示す。
        const sStack = stackProgressFor(absK);
        const liftT = sStack > 0 ? centerEmphasis : 0;
        const liftPx = stackLiftMaxPx * liftT * sStack;
        return {
          xPercent: -50,
          yPercent: -50,
          x: sStack > 0 ? lerp(wheelX, k * stackPitchPx, sStack) : wheelX,
          y: -liftPx,
          rotation: 0,
          scale:
            sStack > 0 ? lerp(wheelScale, stackCardScale, sStack) : wheelScale,
          opacity: 1,
          zIndex: Math.round(
            sStack > 0
              ? lerp(wheelZIndex, 1000 + k + liftT * (N + 1), sStack)
              : wheelZIndex,
          ),
        };
      });
    }

    // 1段階目: morph 中の半径
    const morphR = lerp(circleRadius, fanRadius, t);
    // 2段階目: 回転開始後、Phase 2 の R 目標値（rotationRTarget）まで広がる
    const rotationT = Math.min(
      1,
      Math.abs(fractionalCenter) / ROTATION_R_RAMP_STEPS,
    );

    const scale = lerp(effectiveScaleCircle, scaleFan, t);
    const wEff = cardSize.width * scale;
    const halfPitchRad = (pitchDeg * Math.PI) / 360;
    const sinHalfPitch = Math.sin(halfPitchRad);

    // Phase 1 終端の R: 200% / 50% カード幅クランプを R 自体で担保（均等配置を維持）。
    let phase1R = morphR;
    if (sinHalfPitch > 0) {
      const phase1RMin = (MIN_GAP_RATIO * wEff) / (2 * sinHalfPitch);
      const phase1RMax = (MAX_GAP_RATIO * wEff) / (2 * sinHalfPitch);
      phase1R = Math.min(phase1RMax, Math.max(phase1RMin, morphR));
    }

    // Phase 2 進行で R は viewport の ROTATION_R_VIEWPORT_RATIO 倍まで広がる。
    // 200% 制限はこの段階では R ではなく pitch 縮減で守る。
    const R = lerp(phase1R, rotationRTarget, easeInOut(rotationT));

    // θ_i の動的計算。
    //   k = (i - fractionalCenter) を `[-N/2, +N/2]` にラップして「中心からの最短周回距離」
    //   として扱う。
    //
    //  - Phase 1 終端まで（rotationT=0）: 均等配置 thetaUniform = k * pitchDeg。
    //  - Phase 2 進行（rotationT→1）: 「線形 + 外側多項式」累積関数 thetaVisible:
    //      basePitchDeg = 2 * asin(BASE_GAP_RATIO * wEff / (2R))    （中央 100% pitch）
    //      remainder    = max(0, 180 - basePitchDeg * halfN)      （多項式項に割り当てる残り）
    //      thetaVisible(k) = sign(k) * [
    //         basePitchDeg * |k|                                 （中央線形 = 100% で詰まる）
    //       + remainder * (|k| / halfN)^FAN_DISTRIBUTION_POWER   （外側で多項式加速）
    //      ]
    //    中央 (|k|=0,1) は basePitch（≈ 100% カード幅）で滑らかに線形、|k| が halfN に
    //    近づくほど多項式で急激に加速し、|k|=halfN でちょうど 180°（画面最奥）に到達する。
    //    ループ境界で θ が ±180° で連続するため、k のラップ点で視覚ジャンプは起きない。
    //  - 両者を rotationT で easeInOut lerp して、Phase 1 → Phase 2 の遷移をスムーズに。
    let basePitchDeg = pitchDeg;
    if (R > 0) {
      const sinHalfBase = Math.min(1, (BASE_GAP_RATIO * wEff) / (2 * R));
      basePitchDeg = (Math.asin(sinHalfBase) * 360) / Math.PI;
    }
    const eased = easeInOut(rotationT);
    const halfN = N / 2;
    // |k|=halfN で thetaVisible が 180° に到達するように、線形項で消費しきれない
    // 残角度を多項式項に割り当てる。線形項単独で 180° を超える場合（カード数少なめ）
    // は remainder=0 となり、|k|=halfN 以前に飽和（min 180°）する。
    const remainderToLoop = Math.max(0, 180 - basePitchDeg * halfN);

    return Array.from({ length: N }, (_, i) => {
      // (i - fractionalCenter) を [-halfN, +halfN] にラップ（最短周回）。
      const rawK = i - fractionalCenter;
      const k = ((((rawK + halfN) % N) + N) % N) - halfN;
      const thetaUniform = k * pitchDeg;
      const sign = Math.sign(k);
      const absK = Math.abs(k);
      const linearPart = basePitchDeg * absK;
      const nonlinearPart =
        halfN > 0
          ? remainderToLoop * Math.pow(absK / halfN, FAN_DISTRIBUTION_POWER)
          : 0;
      // 線形項のみで 180° を超える場合に備えて min で飽和（保険）。
      const thetaVisible =
        sign * Math.min(180, linearPart + nonlinearPart);
      const thetaDegRaw = lerp(thetaUniform, thetaVisible, eased);
      const wheelThetaDeg = normalizeAngle(thetaDegRaw);
      // active（中央 θ=0）が最前面。奥のカードほど低い z-index。
      const wheelZIndex = 1000 - Math.abs(wheelThetaDeg);

      // スタックプレビュー: θ / R / scale を「手札状の扇」の目標値
      // （ピボット半径 stackFanPivotRadiusPx・ピッチ stackFanPitchDeg）へ補間し、
      // z-index は「右のカードが上」の手札順へ遷移する。← / → では中央
      //（θ ≈ 0）を固定したまま扇全体が 1 pitch ずつ回転し、中央のカードだけ
      // カード軸に沿ってピボットの外向きへわずかにせり上げて選択を示す。
      const sStack = stackProgressFor(absK);
      const liftT = sStack > 0 ? easeInOut(Math.max(0, 1 - absK)) : 0;
      const liftPx = stackLiftMaxPx * liftT * sStack;
      const thetaDeg =
        sStack > 0
          ? lerp(wheelThetaDeg, k * stackFanPitchDeg, sStack)
          : wheelThetaDeg;
      const cardR = sStack > 0 ? lerp(R, stackFanPivotRadiusPx, sStack) : R;
      const cardScale =
        sStack > 0 ? lerp(scale, stackCardScale, sStack) : scale;
      const thetaRad = (thetaDeg * Math.PI) / 180;

      // lift はカードの向き（θ）に沿ってピボットから離れる方向へ動かす。
      const x = (cardR + liftPx) * Math.sin(thetaRad);
      const y = cardR - (cardR + liftPx) * Math.cos(thetaRad);
      const rotation = thetaDeg;
      const zIndex = Math.round(
        sStack > 0
          ? lerp(wheelZIndex, 1000 + k + liftT * (N + 1), sStack)
          : wheelZIndex,
      );

      return {
        xPercent: -50,
        yPercent: -50,
        x,
        y,
        rotation,
        scale: cardScale,
        opacity: 1,
        zIndex,
      };
    });
  }, [
    N,
    pitchDeg,
    circleRadius,
    fanRadius,
    rotationRTarget,
    effectiveScaleCircle,
    scaleFan,
    cardSize.width,
    isHorizontal,
    horizontalPitchPx,
    activeCardScale,
    stackPitchPx,
    stackFanPitchDeg,
    stackCardScale,
    stackCardHeightPx,
    stackFanPivotRadiusPx,
  ]);

  const applyInstant = useCallback(() => {
    const targets = computeTargets();
    const entryActive = entryActiveRef.current;
    for (let i = 0; i < N; i += 1) {
      const el = cardRefs.current[i];
      if (!el) continue;
      const target = targets[i];
      if (entryActive) {
        // 入場アニメ中: opacity は GSAP tween で 0→1 を制御するため、
        // ここでは上書きしない（位置 / 回転 / scale はスクロール追従で更新する）。
        gsap.set(el, {
          xPercent: target.xPercent,
          yPercent: target.yPercent,
          x: target.x,
          y: target.y,
          rotation: target.rotation,
          scale: target.scale,
          zIndex: target.zIndex,
        });
      } else {
        gsap.set(el, target);
      }
    }
  }, [N, computeTargets]);

  /**
   * スタック進行度をカード transform と件数ラベルへ反映する。
   * トゥイーンの onUpdate から毎フレーム呼ばれる。ref 経由で常に最新の
   * applyInstant（computeTargets）を参照させ、キーリスナ effect の再購読を防ぐ。
   */
  const syncStackVisuals = useCallback(() => {
    applyInstant();
    const labelEl = stackLabelRef.current;
    if (!labelEl) return;
    const stackProgress = stackStateRef.current.progress;
    labelEl.style.opacity = String(stackProgress);
    labelEl.style.visibility = stackProgress > 0.001 ? "visible" : "hidden";
  }, [applyInstant]);
  const syncStackVisualsRef = useRef(syncStackVisuals);
  syncStackVisualsRef.current = syncStackVisuals;

  /**
   * スタックプレビューの開始 / 解除。進行度 0↔1 を GSAP でトゥイーンし、
   * onUpdate でカード transform と件数ラベルを毎フレーム反映する。
   *  - 開始時は velocityZoom を等倍へ即時リセット（スタックの縮小と二重に
   *    ならないように）。ズームの rAF ループも止める。
   *  - reducedMotion 時はトゥイーンせず瞬時に切り替える（機能は残す）。
   */
  const setStackActive = useCallback(
    (active: boolean) => {
      if (stackActiveRef.current === active) return;
      stackActiveRef.current = active;
      onStackChangeRef.current?.(active);

      if (active) {
        const zoomState = velocityZoomStateRef.current;
        if (zoomState.raf !== null) {
          cancelAnimationFrame(zoomState.raf);
          zoomState.raf = null;
        }
        zoomState.velocity = 0;
        zoomState.zoom = 1;
        if (zoomLayerRef.current) {
          zoomLayerRef.current.style.transform = "";
        }
      }

      stackTweenRef.current?.kill();
      stackTweenRef.current = null;
      const state = stackStateRef.current;
      const target = active ? 1 : 0;
      if (reducedMotion) {
        state.progress = target;
        syncStackVisualsRef.current();
        return;
      }
      stackTweenRef.current = gsap.to(state, {
        progress: target,
        duration: active ? STACK_TWEEN_IN_SEC : STACK_TWEEN_OUT_SEC,
        // 集約は「さっと」立ち上がる power3.out、復帰は滑らかな power2.inOut。
        ease: active ? "power3.out" : "power2.inOut",
        onUpdate: () => syncStackVisualsRef.current(),
      });
    },
    [reducedMotion],
  );

  /**
   * 「左から時計回りに円を描くように」カードを順次出現させるための stagger 順。
   * Phase 0 の `θ_i = i * pitchDeg` を `(-180, 180]` 正規化し、
   * `(θ + 90 + 360) % 360` で「左 (-90°) を 0、上 (0°) を 90、右 (90°) を 180、
   * 下 (180°) を 270」に対応付けて昇順に並べる。配列の各要素は cardRefs の index で、
   * order=0 が左、order が大きくなるにつれ時計回りに進む。
   */
  const entrySweepOrder = useMemo<number[]>(() => {
    if (N === 0) return [];
    const order = Array.from({ length: N }, (_, i) => i);
    order.sort((a, b) => {
      const thetaA = normalizeAngle(a * pitchDeg);
      const thetaB = normalizeAngle(b * pitchDeg);
      const sweepA = (((thetaA + 90) % 360) + 360) % 360;
      const sweepB = (((thetaB + 90) % 360) + 360) % 360;
      return sweepA - sweepB;
    });
    return order;
  }, [N, pitchDeg]);

  /**
   * items 入れ替え / 初期マウント時の入場演出。各カードの opacity を 0→1 に
   * `entrySweepOrder` 順で stagger フェードする（「左から円を描くように」表示）。
   * 呼び出し前後で `entryActiveRef` を true/false に管理し、その間 `applyInstant`
   * が opacity を上書きしないようにする。`reducedMotion` のときは何もしない。
   *
   * 短時間に再発火（連続タグ切替など）された場合は、前回予約していたリセット用
   * setTimeout を解除してから新たに張り直す。アンマウント時にもクリーンアップする。
   */
  const playEntryAnimation = useCallback(() => {
    if (reducedMotion || N === 0) return;
    entryActiveRef.current = true;
    entrySweepOrder.forEach((cardIdx, order) => {
      const el = cardRefs.current[cardIdx];
      if (!el) return;
      gsap.fromTo(
        el,
        { opacity: 0 },
        {
          opacity: 1,
          duration: ENTRY_DURATION_SEC,
          delay: order * ENTRY_STAGGER_SEC,
          ease: "power2.out",
        },
      );
    });
    // 末尾カードの tween が確実に終わったあと、`entryActiveRef` を false に戻す。
    const totalMs =
      ((entrySweepOrder.length - 1) * ENTRY_STAGGER_SEC + ENTRY_DURATION_SEC) *
        1000 +
      80;
    if (entryActiveResetTimerRef.current !== null) {
      window.clearTimeout(entryActiveResetTimerRef.current);
    }
    entryActiveResetTimerRef.current = window.setTimeout(() => {
      entryActiveRef.current = false;
      entryActiveResetTimerRef.current = null;
    }, totalMs);
  }, [N, reducedMotion, entrySweepOrder]);

  // ── scrollTop ヘルパ ──────────────────────────────────
  /**
   * 指定の `cardIndex` を θ=0 (active 位置) に持ってくるための scroller の
   * `scrollTop` を算出する。Phase 2（morph 完了後）の位置に着地する。
   *
   * Phase 2 では unbounded な周回数を扱うため、絶対 cardIndex 位置（`morphDistance +
   * cardIndex * navStepPx`）に固定で向かうと、何周もスクロール後にクリックしたとき
   * 「step 0..N-1 まで巻き戻される」現象が起きる。これを避けるため、現在の周回上で
   * `cardIndex` に最も近い step（最短距離）を target として選ぶ。
   */
  const computeScrollTopForIndex = useCallback(
    (cardIndex: number): number | null => {
      const wrapperEl = wrapperRef.current;
      const scrollerEl = resolvedScrollerRef.current;
      if (!wrapperEl || !scrollerEl) return null;

      const wrapperRect = wrapperEl.getBoundingClientRect();
      const scrollerTop =
        scrollerEl === window
          ? 0
          : (scrollerEl as Element).getBoundingClientRect().top;
      const currentScrollDelta = scrollerTop - wrapperRect.top;

      let targetScrollDelta: number;
      if (currentScrollDelta <= morphDistance || N <= 0) {
        // Phase 0 / Phase 1: morph 始点側。絶対 cardIndex 位置に着地。
        targetScrollDelta = morphDistance + cardIndex * navStepPx;
      } else {
        // Phase 2: 現在の周回上で「最も近い cardIndex 位置」を探す。
        // 最大で ±N/2 step ぶんしか動かない smooth scroll になる。
        const currentFractionalCenter =
          (currentScrollDelta - morphDistance) / navStepPx;
        const currentRoundedStep = Math.round(currentFractionalCenter);
        const currentMod = ((currentRoundedStep % N) + N) % N;
        let diff = cardIndex - currentMod;
        if (diff > N / 2) diff -= N;
        if (diff < -N / 2) diff += N;
        const targetStep = currentRoundedStep + diff;
        targetScrollDelta = morphDistance + targetStep * navStepPx;
      }

      const delta = targetScrollDelta - currentScrollDelta;
      const currentScrollTop =
        scrollerEl === window
          ? window.scrollY
          : (scrollerEl as Element).scrollTop;
      return currentScrollTop + delta;
    },
    [morphDistance, navStepPx, N],
  );

  const scrollToCardIndex = useCallback(
    (cardIndex: number) => {
      const scrollerEl = resolvedScrollerRef.current;
      const newScrollTop = computeScrollTopForIndex(cardIndex);
      if (!scrollerEl || newScrollTop === null) return;
      // クリックでの絶対移動は、直前のキー移動の着地先予約を上書きする。
      pendingStepTargetRef.current = null;
      syncLockedUntilRef.current = performance.now() + SCROLL_SYNC_LOCK_MS;
      // ナビゲーション起点の smooth scroll ではズームアウトさせない。
      // コントローラには settling 経由の着地を宣言する（settle 検知を一元化）。
      suppressVelocityZoomForNav();
      gestureControllerRef.current?.beginNavigation();
      // Window と Element はどちらも `scrollTo(ScrollToOptions)` をサポート。
      scrollerEl.scrollTo({ top: newScrollTop, behavior: "smooth" });
    },
    [computeScrollTopForIndex, suppressVelocityZoomForNav],
  );

  /**
   * キーボード ← / → 用に、現在のスクロール位置から「rotation step を ±1」進める。
   * 絶対 cardIndex への jump（= `scrollToCardIndex`）ではなく相対移動なので、
   * 何周しても自然に loop し、ホイールも 1 pitch ぶんだけ回転する。
   *
   * Phase 2 に到達すると `updateFromScroll` 側でステルス前進が走り、currentStep が
   * 常に `LOOP_INITIAL_ADVANCE_LOOPS * N` 付近の大きい値となるため、direction=-1 でも
   * `nextStep = currentStep - 1` が効き、左右で対称的な smooth scroll になる。
   * 例外的に Phase 0 / Phase 1 にいる状態で left を押された場合のみ wrap として
   * 前方向 `N - 1` step に着地（旧挙動）。
   *
   * activeIndex 更新はここでは行わず、scroll listener 側で「中央のカード」と同期する。
   * これによりキー押しっぱなしでも「視覚中央 = 選択中」が常に一致する。
   * （`scrollToCardIndex` のような jump はロックして中間値で activeIndex が揺れる
   * のを防ぐが、こちらは連続キー入力時の中央 = 選択追従を優先するためロックしない）。
   */
  const advanceScrollByOneStep = useCallback(
    (direction: 1 | -1) => {
      const scrollerEl = resolvedScrollerRef.current;
      const wrapperEl = wrapperRef.current;
      if (!scrollerEl || !wrapperEl) return;

      const wrapperRect = wrapperEl.getBoundingClientRect();
      const scrollerTop =
        scrollerEl === window
          ? 0
          : (scrollerEl as Element).getBoundingClientRect().top;
      const currentScrollDelta = scrollerTop - wrapperRect.top;
      const currentScrollTop =
        scrollerEl === window
          ? window.scrollY
          : (scrollerEl as Element).scrollTop;

      // currentStep: 0 = morph 終端 / rotation 起点。負にはしない（scroll が負を持てないため）。
      const currentStep =
        currentScrollDelta > morphDistance
          ? Math.round((currentScrollDelta - morphDistance) / navStepPx)
          : 0;

      let nextStep: number;
      if (direction === 1) {
        nextStep = currentStep + 1;
      } else if (currentStep > 0) {
        nextStep = currentStep - 1;
      } else {
        // step 0 から backward は逆方向に逃げ場が無いので、wrap 用に
        // 前方向 N-1 step ジャンプして activeIndex の wrap を視覚に追従させる。
        nextStep = N - 1;
      }

      const targetScrollDelta = morphDistance + nextStep * navStepPx;
      const newScrollTop =
        currentScrollTop + (targetScrollDelta - currentScrollDelta);

      // 着地先を控えておき、スクロール中に Enter が来ても「これから中央に来る
      // カード」を決定できるようにする（scroll listener の追従待ちで 1 枚ぶん
      // 巻き戻るのを防ぐ）。
      if (N > 0) {
        pendingStepTargetRef.current = {
          index: ((nextStep % N) + N) % N,
          expiresAt: performance.now() + SCROLL_SYNC_LOCK_MS,
        };
      }

      // 左右キーのステップ移動ではズームアウトさせない（連打 / 押しっぱなしで
      // ズームがバウンスし続けると目が疲れるため）。
      // コントローラには settling 経由の着地を宣言する（settle 検知を一元化）。
      suppressVelocityZoomForNav();
      gestureControllerRef.current?.beginNavigation();
      scrollerEl.scrollTo({ top: newScrollTop, behavior: "smooth" });
    },
    [morphDistance, navStepPx, N, suppressVelocityZoomForNav],
  );

  // 親から 1 step 移動を呼び出せるように ref へ公開する（advanceStepRef prop）
  useEffect(() => {
    if (!advanceStepRef) return;
    advanceStepRef.current = advanceScrollByOneStep;
    return () => {
      advanceStepRef.current = null;
    };
  }, [advanceStepRef, advanceScrollByOneStep]);

  /**
   * 中央のカードを選択確定し、`confirm` 指定時は「決定」（onConfirm = 遷移などの
   * 主アクション）も発火する。Stage フォーカス時の Enter / Space とグローバル
   * Enter（globalKeyboardNav）で共用する。
   *
   * ← / → の smooth scroll がまだ飛んでいる最中は `activeIndex` が追従前（古いカード）
   * なので、着地先（`pendingStepTargetRef`）を優先して決定する。そのうえで
   * `activeIndex` も即座に着地先へ揃え、「決定した 1 枚」と「選択表示」が食い違わない
   * ようにする。
   * @returns カードが存在し確定できたか
   */
  const commitActiveItem = useCallback((options: { confirm: boolean }) => {
    const pending = pendingStepTargetRef.current;
    const usePending = pending !== null && performance.now() < pending.expiresAt;
    const index = usePending ? pending.index : activeIndexRef.current;
    const item = itemsRef.current[index];
    if (!item) return false;
    if (usePending) {
      pendingStepTargetRef.current = null;
      activeIndexRef.current = index;
      setActiveIndex(index);
    }
    onChangeRef.current(item.id, item);
    if (options.confirm) onConfirmRef.current?.(item.id, item);
    return true;
  }, []);

  // ── グローバル ← / → / Enter（globalKeyboardNav prop） ─────────────
  // Stage にフォーカスが無くても（ページ遷移直後など）左右キーでホイールを送り、
  // Enter で中央カードを決定（onConfirm）できるようにする。
  // ↑ / ↓ はページスクロールと、Space はページスクロール・入力と競合するため対象外。
  useEffect(() => {
    if (!globalKeyboardNav || disabled || N === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isArrow = e.key === "ArrowLeft" || e.key === "ArrowRight";
      if (!isArrow && e.key !== "Enter") return;
      if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (e.isComposing || e.defaultPrevented) return;
      // 入力欄でのカーソル移動・改行・IME 確定を奪わない
      if (isTypingTarget(e.target)) return;
      // 重ねて開いたモーダル表示中はそちらの操作を優先する
      if (hasBlockingDialog(stageRef.current)) return;
      // Stage フォーカス時は onKeyDown（handleStageKeyDown）が処理するため
      // 二重発火を防ぐ
      if (stageRef.current?.contains(document.activeElement)) return;

      if (isArrow) {
        e.preventDefault();
        advanceScrollByOneStep(e.key === "ArrowRight" ? 1 : -1);
        return;
      }

      // Enter（決定）: ボタン等にフォーカスがあるときはその要素の Enter 動作を
      // 優先し、どこにもフォーカスが無い（= body）ときだけ反応する。
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement)
        return;
      // 押しっぱなしのキーリピートで決定（遷移）を連発しない
      if (e.repeat) return;
      if (commitActiveItem({ confirm: true })) e.preventDefault();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [globalKeyboardNav, disabled, N, advanceScrollByOneStep, commitActiveItem]);

  // ── Stage 幅の測定（半径 auto 算出に使う） ─────────────
  useLayoutEffect(() => {
    const stageEl = stageRef.current;
    if (!stageEl) return;
    const update = () => {
      const w = stageEl.getBoundingClientRect().width;
      if (w > 0 && Math.abs(w - measuredStageWidth) > 0.5) {
        setMeasuredStageWidth(w);
      }
    };
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(stageEl);
    return () => ro.disconnect();
    // measuredStageWidth は依存に入れない（観測 → setState のフィードバックを避ける）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── スクローラ解決 + スクロールリスナ ──────────────────
  useLayoutEffect(() => {
    if (N === 0) {
      notifySettled(false);
      return;
    }

    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return;

    const resolvedScroller: Element | Window =
      scroller ??
      wrapperEl.closest("[data-offer-overlay-scroll]") ??
      window;
    resolvedScrollerRef.current = resolvedScroller;

    // velocityZoom が無効（未指定 / reducedMotion 等）のときは、
    // 有効時代の残留（reducedMotion への切替直前にズーム中だった等）を確実に消す。
    // 有効時の状態は effect 再実行（activeIndex 連動の onChange 変化等）を跨いで
    // 維持したいので、ここ以外ではリセットしない。
    const zoomState = velocityZoomStateRef.current;
    if (!velocityZoomEnabled) {
      if (zoomState.raf !== null) {
        cancelAnimationFrame(zoomState.raf);
        zoomState.raf = null;
      }
      zoomState.velocity = 0;
      zoomState.zoom = 1;
      zoomState.lastDelta = null;
      if (zoomLayerRef.current) {
        zoomLayerRef.current.style.transform = "";
      }
    }

    if (reducedMotion) {
      // モーション無効時は扇モード固定 + activeIndex のカードを上端に置く。
      progressRef.current = 1;
      fractionalCenterRef.current = activeIndexRef.current;
      applyInstant();
      // 静的表示 = 常に中央整列済みとして扱う。
      notifySettled(true);
      return;
    }

    // ── カード 1 枚: ホイール演出なしの静的固定表示 ──
    // スクロール距離（ScrollSpacer / wrapper 高さ）は確保済みの 0 のため、
    // scroll listener もジェスチャ転送も張らない。breakOut 時は Stage を
    // 「アクティブカード位置（fanCenteredTop 相当）」へ固定し、リサイズにだけ追従する。
    if (isStaticSingle) {
      progressRef.current = 1;
      fractionalCenterRef.current = 0;
      applyInstant();
      // カード 1 枚の固定表示 = 常に中央整列済みとして扱う。
      notifySettled(true);

      const singleStageEl = stageRef.current;
      if (!effectiveBreakOut || !singleStageEl) return;
      const updateSingleCardPosition = () => {
        const viewportCenterY = verticalCenterY ?? window.innerHeight / 2;
        const { left, width } = computeBreakoutHorizontalBounds(
          wrapperEl,
          breakOutInsetPx,
        );
        singleStageEl.style.top = `${viewportCenterY - cardCenterY}px`;
        singleStageEl.style.left = `${left}px`;
        singleStageEl.style.width = `${width}px`;
      };
      updateSingleCardPosition();
      window.addEventListener("resize", updateSingleCardPosition, {
        passive: true,
      });
      return () => {
        window.removeEventListener("resize", updateSingleCardPosition);
      };
    }

    // 少数カード時はモーフを省略し、最初から扇形 (progress=1) で表示する。
    progressRef.current = skipIntro ? 1 : 0;
    fractionalCenterRef.current = 0;
    applyInstant();

    const stageEl = stageRef.current;

    // モード切替時に inline スタイルが残らないようリセットしておく。
    if (stageEl && !effectiveBreakOut) {
      stageEl.style.top = "";
      stageEl.style.left = "";
      stageEl.style.width = "";
    }

    /**
     * ジェスチャコントローラ。breakOut ブロック（後方）で生成される。
     * `updateFromScroll` が整列状態（aligned）を通知するため、ここで束縛しておく。
     */
    let gestureController: WheelGestureController | null = null;

    /**
     * breakOut 時の Stage の viewport-座標位置を sticky 相当に同期する。
     *
     * 縦中央の基準は **viewport 中央** （`window.innerHeight / 2`）を採用する。
     * GlobalHeader / ProjectBanner / BottomAppBar 等で部分的に隠れる可能性は
     * あるが、要件「最初の縦位置は完全ビューポート高さの真ん中」を優先する。
     * （`effectiveScrollerHeight` は円が収まるかの shrink 判定でのみ使う。）
     *
     * sticky 区間では、progress に応じて stageTop を 2 つの理想位置の間で lerp する:
     *   - 円モード（progress=0）の理想位置 `circleCenteredTop`:
     *       「円の中心」が viewport 縦中央に来る位置
     *       (= innerHeight/2 - (cardCenterY + circleRadius))
     *   - 扇モード（progress=1）の理想位置 `fanCenteredTop`:
     *       「アクティブカード（θ=0）」が viewport 縦中央に来る位置
     *       (= innerHeight/2 - cardCenterY)
     *
     * 5 枚以下（useCircleInitial=false）のときは初期から扇モードなので、
     * 円中心の概念がない。この場合は従来どおり `stickyTop ↔ fanCenteredTop` の
     * lerp を使う。
     */
    const updateBreakOutPosition = () => {
      if (!effectiveBreakOut || !stageEl) return;
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const scrollerRect =
        resolvedScroller === window
          ? { top: 0 }
          : (resolvedScroller as Element).getBoundingClientRect();

      const stickyTop = scrollerRect.top;
      const viewportCenterY = verticalCenterY ?? window.innerHeight / 2;
      const fanCenteredTop = viewportCenterY - cardCenterY;
      const circleCenteredTop =
        viewportCenterY - (cardCenterY + circleRadius);
      // 5 枚以下の扇モード初期化時は円中心の概念がないので従来どおり stickyTop を起点に。
      const initialTop = useCircleInitial ? circleCenteredTop : stickyTop;

      let stageTop: number;
      if (wrapperRect.top >= scrollerRect.top) {
        // sticky 区間外（= スクロール演出が始まる前 / 上方向に戻して抜けた直後）。
        // 円モードでは「円中心が viewport 中央」に固定する（wrapper の DOM 位置は
        // 完全に無視）。breakOut は position: fixed なので flow から切り離されており、
        // 視覚位置は wrapper 位置に縛られない。
        //
        // これにより:
        //   1. 初期表示で円中心が viewport 縦中央になる（要件: 「初期では、ビューポートの
        //      真ん中に中心点の円を描く」）。
        //   2. sticky 区間内 progress=0 の `circleCenteredTop` と完全連続。
        //   3. 上方向に戻して sticky 区間を抜けた瞬間も同じ位置を保つので、
        //      「中心が下方向にずれる」現象が消える。
        //
        // 5 枚以下の扇モード（useCircleInitial=false）は円中心の概念が無いため
        // 従来どおり wrapper top に追従する。
        stageTop = useCircleInitial ? circleCenteredTop : wrapperRect.top;
      } else if (wrapperRect.bottom - stageHeight >= scrollerRect.top) {
        const t = easeInOut(progressRef.current);
        stageTop = lerp(initialTop, fanCenteredTop, t);
      } else {
        stageTop = wrapperRect.bottom - stageHeight;
      }

      const { left, width } = computeBreakoutHorizontalBounds(
        wrapperEl,
        breakOutInsetPx,
      );

      stageEl.style.top = `${stageTop}px`;
      stageEl.style.left = `${left}px`;
      stageEl.style.width = `${width}px`;
    };

    /**
     * scrollTop を steps 周ぶん（正なら前進、負なら巻き戻し）一気に動かす。
     * `pitchDeg = 360/N` の整数倍ぶんジャンプするので、`k = ((rawK + halfN) % N + N) %
     * N - halfN` のラップにより視覚位置 / activeIndex (mod N) は不変。
     * 直接 scrollTop に書き込んで scroll event を再発火させ、syncLockedUntilRef を瞬間
     * 立てて activeIndex 同期側の暴れを防ぐ。
     */
    const applyStealthScroll = (loops: number, extraPx = 0) => {
      if ((loops === 0 && extraPx === 0) || N === 0) return;
      const deltaPx = loops * N * navStepPx + extraPx;
      syncLockedUntilRef.current = performance.now() + 50;
      if (resolvedScroller === window) {
        window.scrollBy(0, deltaPx);
      } else {
        (resolvedScroller as Element).scrollTop += deltaPx;
      }
    };

    // ── velocityZoom: 速度サンプリング + 倍率追従 rAF ループ ──────
    // scroll イベントごとに scrollDelta の差分から速度(px/ms)を EMA で平滑化し、
    // rAF ループで「減衰 → 目標倍率 → 現在倍率の lerp 追従」を回す。イベントが
    // 途絶えると velocity が毎フレーム減衰し、倍率は自然に等倍へ戻って停止する。
    const stopZoomLoop = () => {
      if (zoomState.raf !== null) {
        cancelAnimationFrame(zoomState.raf);
        zoomState.raf = null;
      }
    };
    const zoomFrame = () => {
      zoomState.velocity *= VELOCITY_ZOOM_DECAY_PER_FRAME;
      if (Math.abs(zoomState.velocity) < 0.01) zoomState.velocity = 0;
      // 凹型（指数 < 1）の応答カーブ: 低速域の反応を持ち上げ、軽いスクロール
      // でもはっきりズームアウトさせる。フル深度には maxVelocity が必要。
      const intensity = Math.min(
        1,
        Math.pow(
          zoomState.velocity / velocityZoomMaxVelocity,
          VELOCITY_ZOOM_RESPONSE_EXPONENT,
        ),
      );
      const targetZoom =
        1 - (1 - velocityZoomEffectiveMinScale) * intensity;
      zoomState.zoom += (targetZoom - zoomState.zoom) * VELOCITY_ZOOM_EASE;

      const settled =
        zoomState.velocity === 0 && Math.abs(zoomState.zoom - 1) < 0.001;
      const layerEl = zoomLayerRef.current;
      if (layerEl) {
        layerEl.style.transform = settled ? "" : `scale(${zoomState.zoom})`;
      }
      if (settled) {
        zoomState.zoom = 1;
        zoomState.raf = null;
        return;
      }
      zoomState.raf = requestAnimationFrame(zoomFrame);
    };
    const sampleVelocityZoom = (scrollDelta: number) => {
      const now = performance.now();
      const prevDelta = zoomState.lastDelta;
      const dt = now - zoomState.lastTime;
      zoomState.lastDelta = scrollDelta;
      zoomState.lastTime = now;
      // スタックプレビュー中はズームさせない（スタックの縮小と二重に見えるため）。
      if (stackActiveRef.current) return;
      // arc の Phase 1（円 → 扇モーフ中）はズームしない。円モードは全カードが
      // 既に見えており、モーフの縮小と重なると二重に縮んで見えるため。
      // 進行中のズームは rAF ループの減衰で自然に等倍へ戻る。
      if (!isHorizontal && progressRef.current < 1) return;
      // ナビゲーション（左右キー / カードクリック）起点の smooth scroll 中は
      // 速度を蓄積しない。基準値（lastDelta / lastTime）は上で更新済みなので、
      // 時限明けの最初のサンプルも直前イベントとの差分から正しく始まる。
      // 進行中のズームは rAF ループの減衰で自然に等倍へ戻る。
      if (now < velocityZoomNavSuppressUntilRef.current) return;
      if (prevDelta === null || dt <= 0) return;
      const moved = Math.abs(scrollDelta - prevDelta);
      if (moved === 0) return;
      // ステルスジャンプ（ループ巻き戻し / 初回前進）による瞬間テレポートは
      // 実スクロールではないので速度計測から除外する。
      if (moved > VELOCITY_ZOOM_MAX_EVENT_DELTA_PX) return;
      const instantVelocity = moved / dt;
      zoomState.velocity =
        zoomState.velocity * (1 - VELOCITY_ZOOM_SAMPLE_WEIGHT) +
        instantVelocity * VELOCITY_ZOOM_SAMPLE_WEIGHT;
      if (zoomState.raf === null) {
        zoomState.raf = requestAnimationFrame(zoomFrame);
      }
    };
    if (velocityZoomEnabled && zoomState.raf === null) {
      // effect 再実行（スクロール中の activeIndex 変化で onChange の identity が
      // 変わる等）で cleanup が rAF を止めた直後の再開。ズーム中のまま放置される
      // ことを防ぎ、等倍への復帰（または追従継続）を引き継ぐ。
      if (zoomState.zoom !== 1 || zoomState.velocity !== 0) {
        zoomState.raf = requestAnimationFrame(zoomFrame);
      }
    }

    const updateFromScroll = () => {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const scrollerTop =
        resolvedScroller === window
          ? 0
          : (resolvedScroller as Element).getBoundingClientRect().top;
      const scrollDelta = scrollerTop - wrapperRect.top;

      if (velocityZoomEnabled) {
        sampleVelocityZoom(scrollDelta);
      }

      let nextProgress: number;
      let nextFractionalCenter: number;

      if (!skipIntro && scrollDelta <= 0) {
        nextProgress = 0;
        nextFractionalCenter = 0;
        // Phase 0 に戻ったら次回 Phase 2 入る瞬間に再びステルス前進したい。
        phase2InitialAdvancedRef.current = false;
      } else if (!skipIntro && scrollDelta < morphDistance) {
        // Phase 1: 円 → 扇 のモーフィング。wheel はまだ回さない。
        nextProgress = scrollDelta / morphDistance;
        nextFractionalCenter = 0;
        phase2InitialAdvancedRef.current = false;
      } else {
        // Phase 2: 扇モード維持 + wheel 回転。
        // fractionalCenter は unbounded で進む。活性カードは round mod N で循環するので
        // 何周しても活性 ID は正しく回り、視覚も `k = ((rawK + halfN) % N + N) % N - halfN`
        // のラップで連続。
        nextProgress = 1;
        const navDelta = scrollDelta - morphDistance;
        nextFractionalCenter = Math.max(0, navDelta / navStepPx);

        // ステルス前進（Phase 2 初回到達時）: scroller を「ループ中央」に置き、
        // 左右キー操作の前後対称性を確保（=「逆に戻る」現象の回避）。
        if (
          !phase2InitialAdvancedRef.current &&
          LOOP_INITIAL_ADVANCE_LOOPS > 0
        ) {
          phase2InitialAdvancedRef.current = true;
          // skipIntro（少数カード）のマウント直後は scrollDelta < morphDistance で
          // navDelta が負になっている。その不足分も同時に吸収しないと、前進後の
          // fractionalCenter が `-morphDistance / navStepPx` だけズレて
          // 「中央のカードが半端な位置に止まり、選択とも一致しない」初期不整合が
          // 起きるため、ちょうど `LOOP_INITIAL_ADVANCE_LOOPS * N`（= 整列位置）に
          // 着地させる。
          applyStealthScroll(
            LOOP_INITIAL_ADVANCE_LOOPS,
            Math.max(0, -navDelta),
          );
          // 前進後の scroll event で再計算されるので、ここでは visual 更新を打ち切る。
          return;
        }

        // ステルス巻き戻し: scroller 末端到達による「高速戻り / カクつき」を回避する。
        // fractionalCenter が `LOOP_ANCHOR_LOOPS * N + N` を超えたら 1 周ぶん巻き戻す。
        if (nextFractionalCenter > LOOP_ANCHOR_LOOPS * N + N) {
          applyStealthScroll(-1);
          return;
        }
      }

      // 「カードステップ上に整列しているか」をコントローラへ通知する。
      // settling（ナッジ）の到達検知と、外部要因で中央から外れた際の
      // 実態同期（card / detent → list）に使う。
      if (gestureController) {
        const alignedToStep =
          nextProgress >= 1 &&
          Math.abs(nextFractionalCenter - Math.round(nextFractionalCenter)) *
            navStepPx <
            1;
        gestureController.notifyScroll(alignedToStep);
      }

      const visualChanged =
        Math.abs(progressRef.current - nextProgress) > 0.001 ||
        Math.abs(fractionalCenterRef.current - nextFractionalCenter) > 0.001;

      if (visualChanged) {
        progressRef.current = nextProgress;
        fractionalCenterRef.current = nextFractionalCenter;
        applyInstant();
      }

      // breakOut 時は Stage の viewport 座標位置も常に追従させる。
      updateBreakOutPosition();

      // activeIndex 同期: 「視覚中央のカード」を常に「活性 / 選択中」と一致させる。
      // クリック起動の smooth-scroll（= scrollToCardIndex）が走っている間はロック中で
      // 中間 round 値による activeIndex の暴れを防ぐ。キー操作（advanceScrollByOneStep）
      // はロックを立てないため scroll に追従して中央 = 選択が常に同期する。
      // fractionalCenter は無限に進むが、mod N で活性 ID は循環する。
      if (
        nextProgress >= 1 &&
        performance.now() >= syncLockedUntilRef.current
      ) {
        const rounded = Math.round(nextFractionalCenter);
        const wrapped = N > 0 ? ((rounded % N) + N) % N : 0;
        // キー移動の着地先に到達したら「これから中央に来るカード」の予約を外す。
        if (pendingStepTargetRef.current?.index === wrapped) {
          pendingStepTargetRef.current = null;
        }
        if (wrapped !== activeIndexRef.current) {
          activeIndexRef.current = wrapped;
          setActiveIndex(wrapped);
          // 中央のカードを「選択（value）」として親に通知。これにより aria-checked /
          // 青ボーダー等の選択表現が常に視覚中央に追従する。
          const item = items[wrapped];
          if (item) onChangeRef.current(item.id, item);
        }
      }
    };

    resolvedScroller.addEventListener("scroll", updateFromScroll, {
      passive: true,
    });
    window.addEventListener("resize", updateFromScroll, { passive: true });
    updateFromScroll();

    // 入場アニメーションの発火判定:
    //  - `items` 参照が前回と変わった（= 初回マウント or タグ切替で件数 / 種類入替）
    //  - 現状が Phase 0（scrollTop≈0 で morph 進行ゼロ）
    //  - reducedMotion ではない
    // を満たすときだけ「左から円を描くように」stagger フェードを発火する。
    // Phase 2 から smooth scroll で巻き戻る途中で items が入れ替わった場合は
    // ここで progressRef > 0 のため発火しない（smooth scroll 自体が演出になる）。
    const itemsChanged = lastEntryItemsRef.current !== items;
    lastEntryItemsRef.current = items;
    if (
      itemsChanged &&
      !reducedMotion &&
      !skipIntro &&
      progressRef.current < 0.01 &&
      Math.abs(fractionalCenterRef.current) < 0.01
    ) {
      playEntryAnimation();
    }

    // breakOut モード時は Stage が position:fixed なので、Stage 上で起きた wheel /
    // touch のスクロール意思は、ブラウザのデフォルト処理だけでは下層の
    // scroller（OverlayBody）に届かないことがある。すべての入力 delta を
    // WheelGestureController（list / settling / card / detent のステートマシン）に
    // 集約し、リスト回転（scrollTop 転送）・中央ナッジ・カード内スクロール・
    // 「ため」を一元管理する。ネイティブスクロールとの混在はやめ、Stage 上の
    // ジェスチャは常に preventDefault して自前でルーティングする
    //（一度 preventDefault するとネイティブスクロールが復活しない問題の根治）。
    let detachStageScrollForward: (() => void) | undefined;
    if (
      effectiveBreakOut &&
      stageEl &&
      resolvedScroller !== window
    ) {
      const scrollerElement = resolvedScroller as Element;

      // 指を離した後の慣性スクロール用 rAF ハンドル。
      let momentumRaf: number | null = null;
      const cancelMomentum = () => {
        if (momentumRaf !== null) {
          cancelAnimationFrame(momentumRaf);
          momentumRaf = null;
        }
      };

      /**
       * 進行中の smooth scroll（スナップ / ナビゲーション）を現在位置で打ち切る。
       * タッチ開始時に呼ばないと、スナップアニメーションが指のドラッグと
       * scrollTop を取り合い、「突っかかる / スワイプと逆方向へ戻る」原因になる。
       */
      const stopSmoothScroll = () => {
        // jsdom は Element.scrollTo を実装しないため存在チェックする。
        if (typeof scrollerElement.scrollTo !== "function") return;
        scrollerElement.scrollTo({
          top: scrollerElement.scrollTop,
          behavior: "auto",
        });
      };

      /**
       * document レベル（capture）で受けたジェスチャがこのホイールの操作対象か。
       * Stage 内、または `gestureSurfaceKey` が一致するジェスチャ面
       * （`data-wheel-gesture-surface` を持つ body ポータルのオーバーレイ等）を
       * 対象にする。合成イベントの転送（Safari は Touch を構築できない）に
       * 頼らず、オーバーレイ上のスワイプ / ホイールを直接受けるための仕組み。
       */
      const isEligibleGestureTarget = (
        target: EventTarget | null,
      ): boolean => {
        if (!(target instanceof Node)) return false;
        if (stageEl.contains(target)) return true;
        if (!gestureSurfaceKey) return false;
        const el =
          target instanceof Element ? target : target.parentElement;
        return (
          el?.closest(
            `[${WHEEL_GESTURE_SURFACE_ATTR}="${gestureSurfaceKey}"]`,
          ) != null
        );
      };

      /**
       * 最寄りステップへのナッジ（スナップ）依頼。コントローラの requestSnap。
       * arc / horizontal の両レイアウトで共通に使う（従来 arc のホイール操作には
       * アイドルスナップが無く、カードが中間位置で止まり得た）。
       */
      const requestSnap = (): SnapRequestResult => {
        const wrapperElLocal = wrapperRef.current;
        if (!wrapperElLocal) return "unavailable";
        const wrapperRectLocal = wrapperElLocal.getBoundingClientRect();
        const scrollerTopLocal =
          scrollerElement.getBoundingClientRect().top;
        const currentScrollDelta = scrollerTopLocal - wrapperRectLocal.top;
        // morph 区間（円→扇）に居る間はスナップしない（演出を阻害しないため）。
        // skipIntro（最初から扇形）は fractionalCenter が 0 に張り付いており
        // 視覚上は整列済みなので aligned として扱う。
        if (currentScrollDelta < morphDistance) {
          return skipIntro ? "aligned" : "unavailable";
        }
        const step = Math.round(
          (currentScrollDelta - morphDistance) / navStepPx,
        );
        const targetScrollDelta = morphDistance + step * navStepPx;
        if (Math.abs(targetScrollDelta - currentScrollDelta) < 0.5) {
          return "aligned";
        }
        const newScrollTop =
          scrollerElement.scrollTop +
          (targetScrollDelta - currentScrollDelta);
        // 着地用の smooth scroll でズームアウトが再発しないようにする。
        suppressVelocityZoomForNav();
        scrollerElement.scrollTo({ top: newScrollTop, behavior: "smooth" });
        return "started";
      };

      /**
       * 中央（選択中）カードの `data-card-scroll` 要素。card / detent 状態の
       * 入力ルーティング先。イベントターゲットではなく「中央カード」から
       * 解決するので、カード外（Stage の余白）でのホイール操作でも中央カードの
       * 本文がスクロールする（= 中央カードにフォーカスが当たる操作感）。
       */
      const getCardScrollEl = (): CardScrollTarget | null => {
        const slotEl = cardRefs.current[activeIndexRef.current];
        if (!slotEl) return null;
        return slotEl.querySelector<HTMLElement>("[data-card-scroll]");
      };

      const controller = new WheelGestureController({
        scrollList: (deltaPx) => {
          // 感度係数はリスト回転にのみ適用する（カード内スクロールは 1:1）。
          scrollerElement.scrollTop += deltaPx * GESTURE_SCROLL_GAIN;
        },
        requestSnap,
        getCardScrollEl,
        onSettleChange: notifySettled,
        handoff: cardScrollHandoff,
      });
      gestureController = controller;
      gestureControllerRef.current = controller;

      const handleWheel = (e: WheelEvent) => {
        if (!isEligibleGestureTarget(e.target)) return;
        // スタックプレビュー中はホイールを回さない（macOS の Shift+スクロール
        // は横 delta に変換されるため、素通しすると意図せず回転する）。
        if (stackActiveRef.current) {
          e.preventDefault();
          return;
        }
        // arc / horizontal 共通で「支配的な軸」の移動量として扱う。
        // トラックパッドの横スワイプでも回せる（上 / 左 = 戻る、下 / 右 = 進む）。
        // breakOut の Stage は touch-action: none の全面オーバーレイで、
        // 横パンと競合するネイティブスクロールは存在しない。
        const delta = dominantWheelDelta(e.deltaX, e.deltaY);
        cancelMomentum();
        controller.input(delta, "wheel");
        e.preventDefault();
      };

      // 上下・左右のどちらで操作しても、最初に優勢になった軸をジェスチャ終了
      // まで固定して仮想スクロールへ変換する（arc / horizontal 共通。従来 arc は
      // 縦軸しか読まず、横スワイプが無反応・斜めスワイプで逆回転していた）。
      let touchStartX: number | null = null;
      let touchStartY: number | null = null;
      let touchLastX: number | null = null;
      let touchLastY: number | null = null;
      let touchAxis: "x" | "y" | null = null;
      // 支配軸方向の速度(px/ms)の指数移動平均。指を離したときの初速に使う。
      // イベント間隔（60Hz / 120Hz）に依存しないよう時間で正規化する。
      let touchVelocityPxMs = 0;
      // 直近の touchmove（または touchstart）の timeStamp。
      let touchLastMoveTime = 0;
      const resetTouchTracking = () => {
        touchStartX = null;
        touchStartY = null;
        touchLastX = null;
        touchLastY = null;
        touchAxis = null;
        touchVelocityPxMs = 0;
        touchLastMoveTime = 0;
      };
      const handleTouchStart = (e: TouchEvent) => {
        if (!isEligibleGestureTarget(e.target)) return;
        // スタックプレビュー中はタッチでの回転も受け付けない。
        if (stackActiveRef.current) return;
        cancelMomentum();
        resetTouchTracking();
        if (e.touches.length !== 1) {
          // マルチタッチはジェスチャとして扱わない。1 本目で立てた保持状態も解く。
          controller.setPointerHeld(false);
          return;
        }
        // スナップ / ナビゲーションの smooth scroll 進行中に指を置いたら、
        // その位置で打ち切ってドラッグとの綱引きを防ぐ。
        stopSmoothScroll();
        controller.setPointerHeld(true);
        const touch = e.touches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchLastX = touch.clientX;
        touchLastY = touch.clientY;
        touchLastMoveTime = e.timeStamp;
      };
      const handleTouchMove = (e: TouchEvent) => {
        // タッチ中にスタックプレビューへ入った場合はジェスチャを打ち切る
        // （touchcancel と同じ扱い。解除後も指を離すまで再開しない）。
        if (stackActiveRef.current) {
          if (touchStartX !== null) {
            controller.setPointerHeld(false);
            resetTouchTracking();
          }
          return;
        }
        if (
          touchStartX === null ||
          touchStartY === null ||
          touchLastX === null ||
          touchLastY === null ||
          e.touches.length !== 1
        ) {
          return;
        }
        const touch = e.touches[0];
        const movedX = touchStartX - touch.clientX;
        const movedY = touchStartY - touch.clientY;

        if (touchAxis === null) {
          touchAxis = resolveTouchAxis(movedX, movedY);
          if (touchAxis === null) return;
        }

        const delta =
          touchAxis === "x"
            ? touchLastX - touch.clientX
            : touchLastY - touch.clientY;
        controller.input(delta, "touch");
        // px/ms の瞬間速度を指数移動平均でならす（60Hz / 120Hz で同じ量になる）。
        const dtMs = Math.max(1, e.timeStamp - touchLastMoveTime);
        touchLastMoveTime = e.timeStamp;
        touchVelocityPxMs = touchVelocityPxMs * 0.6 + (delta / dtMs) * 0.4;
        touchLastX = touch.clientX;
        touchLastY = touch.clientY;
        if (
          Math.max(Math.abs(movedX), Math.abs(movedY)) >=
          TOUCH_CLICK_SUPPRESS_DISTANCE_PX
        ) {
          suppressClickUntilRef.current =
            performance.now() + TOUCH_CLICK_SUPPRESS_MS;
        }
        e.preventDefault();
      };
      const handleTouchEnd = (e: TouchEvent) => {
        // このホイールが追跡していないタッチ（対象外の場所で始まった等）は無視。
        if (touchStartX === null) return;
        controller.setPointerHeld(false);
        // 速度は px/ms → 60fps 基準の px/frame に換算し、従来の scrollTop 単位の
        // 上限・下限チューニングを保つためゲインぶん割り戻して比較する。
        const maxRawVelocity = MOMENTUM_MAX_VELOCITY / GESTURE_SCROLL_GAIN;
        const minRawVelocity = MOMENTUM_MIN_VELOCITY / GESTURE_SCROLL_GAIN;
        // 指を止めてから離した場合は慣性を出さない（その場で止める操作）。
        const stillMs = e.timeStamp - touchLastMoveTime;
        let velocity =
          stillMs > TOUCH_MOMENTUM_HOLD_CANCEL_MS
            ? 0
            : touchVelocityPxMs * MOMENTUM_FRAME_MS;
        velocity = Math.max(
          -maxRawVelocity,
          Math.min(maxRawVelocity, velocity),
        );
        resetTouchTracking();
        if (Math.abs(velocity) < minRawVelocity) {
          controller.endMomentum();
          return;
        }
        // 慣性は rAF の実測経過時間で按分し、リフレッシュレート非依存にする。
        let lastFrameTime = performance.now();
        const stepFrame = (now: number) => {
          // スタックプレビュー中は慣性も打ち切る（山の下でホイールが
          // 回り続けないように）。endMomentum で通常の終端処理へ渡す。
          if (stackActiveRef.current) {
            momentumRaf = null;
            controller.endMomentum();
            return;
          }
          if (Math.abs(velocity) < minRawVelocity) {
            momentumRaf = null;
            controller.endMomentum();
            return;
          }
          const frames =
            Math.min(50, Math.max(1, now - lastFrameTime)) /
            MOMENTUM_FRAME_MS;
          lastFrameTime = now;
          const routed = controller.input(velocity * frames, "momentum");
          if (routed === "absorbed") {
            // 慣性のエネルギーは detent / settling が吸収した。ここで打ち切る。
            momentumRaf = null;
            return;
          }
          velocity *= Math.pow(MOMENTUM_FRICTION, frames);
          momentumRaf = requestAnimationFrame(stepFrame);
        };
        momentumRaf = requestAnimationFrame(stepFrame);
      };
      const handleTouchCancel = () => {
        if (touchStartX === null) return;
        controller.setPointerHeld(false);
        resetTouchTracking();
        cancelMomentum();
        controller.endMomentum();
      };

      // Stage（position: fixed）の上に body ポータルのジェスチャ面（待機ショット
      // チップ等）が重なるため、リスナは document の capture 段で一括して受け、
      // isEligibleGestureTarget で対象を絞る。Stage 直付けだとオーバーレイ上の
      // タッチが届かず、「触る場所によってスワイプが効かない」原因になっていた。
      document.addEventListener("wheel", handleWheel, {
        capture: true,
        passive: false,
      });
      document.addEventListener("touchstart", handleTouchStart, {
        capture: true,
        passive: true,
      });
      document.addEventListener("touchmove", handleTouchMove, {
        capture: true,
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, {
        capture: true,
        passive: true,
      });
      document.addEventListener("touchcancel", handleTouchCancel, {
        capture: true,
        passive: true,
      });

      // マウント / items 入替直後、既にステップ上に静止していれば即 settled に
      // なる（ズレていればナッジで整列する）。円→扇モーフ区間では unavailable と
      // なり、入場演出を阻害しない。
      controller.requestSettle();

      detachStageScrollForward = () => {
        cancelMomentum();
        controller.destroy();
        if (gestureControllerRef.current === controller) {
          gestureControllerRef.current = null;
        }
        gestureController = null;
        document.removeEventListener("wheel", handleWheel, {
          capture: true,
        });
        document.removeEventListener("touchstart", handleTouchStart, {
          capture: true,
        });
        document.removeEventListener("touchmove", handleTouchMove, {
          capture: true,
        });
        document.removeEventListener("touchend", handleTouchEnd, {
          capture: true,
        });
        document.removeEventListener("touchcancel", handleTouchCancel, {
          capture: true,
        });
      };
    }

    return () => {
      resolvedScroller.removeEventListener("scroll", updateFromScroll);
      window.removeEventListener("resize", updateFromScroll);
      detachStageScrollForward?.();
      // velocityZoom の rAF はここでは止めるだけにする。ズーム量・速度・transform は
      // 意図的に維持し、直後の effect 再実行（スクロール中の onChange 変化等）で
      // ループを引き継ぐ。完全な無効化時のリセットは effect 冒頭で行う。
      // （アンマウント時は DOM ごと消えるので残留はない。）
      stopZoomLoop();
    };
  }, [
    N,
    reducedMotion,
    scroller,
    morphDistance,
    navStepPx,
    effectiveBreakOut,
    breakOutInsetPx,
    verticalCenterY,
    stageHeight,
    applyInstant,
    cardCenterY,
    circleRadius,
    useCircleInitial,
    skipIntro,
    isStaticSingle,
    isHorizontal,
    items,
    playEntryAnimation,
    velocityZoomEnabled,
    velocityZoomEffectiveMinScale,
    velocityZoomMaxVelocity,
    suppressVelocityZoomForNav,
    cardScrollHandoff,
    gestureSurfaceKey,
    notifySettled,
  ]);

  // ── スタックプレビュー（Shift ホールドで全カードを中央へ集約） ──
  // stackPreview 指定時のみ document へキーリスナを張る。keyup を取りこぼす
  // 状況（ウィンドウ切替・タブ非表示・クリック操作・他キーとの組み合わせ）でも
  // 押しっぱなし状態が残らないよう、多重の解除経路を用意する。
  useEffect(() => {
    if (!stackPreviewEnabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (stackActiveRef.current) {
        // スタック表示中の ← / →（修飾キーなし。Shift 自体は押下中）は解除せず
        // 「選択の切り替え」として 1 step 送る。通常のホイールと同じく選択
        // カードが中央に留まったまま山（扇）全体が回転する。キーリピートも
        // 受け付ける。
        if (
          (e.key === "ArrowLeft" || e.key === "ArrowRight") &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.altKey
        ) {
          e.preventDefault();
          advanceScrollByOneStep(e.key === "ArrowRight" ? 1 : -1);
          return;
        }
        // Shift 以外のキーが押されたら解除する（⇧M などの既存ショートカット
        // 併用時にスタックが出しゃばらないように）。
        if (e.key !== "Shift") setStackActive(false);
        return;
      }
      // 重ねて開いたモーダルの裏でホイールが山になるのを防ぐ。
      if (hasBlockingDialog(stageRef.current)) return;
      if (shouldEnterStackPreview(e)) setStackActive(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") setStackActive(false);
    };
    const handleWindowBlur = () => setStackActive(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") setStackActive(false);
    };
    const handlePointerDown = () => setStackActive(false);

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("pointerdown", handlePointerDown);
      // アンマウント / 無効化時はトゥイーンを止めて即座に通常配置へ戻す。
      stackTweenRef.current?.kill();
      stackTweenRef.current = null;
      if (stackActiveRef.current) {
        stackActiveRef.current = false;
        onStackChangeRef.current?.(false);
      }
      if (stackStateRef.current.progress !== 0) {
        stackStateRef.current.progress = 0;
        syncStackVisualsRef.current();
      }
    };
  }, [stackPreviewEnabled, setStackActive, advanceScrollByOneStep]);

  // activeIndex の ref 同期。
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // アンマウント時に、入場演出の `entryActiveRef` リセット用タイマを解除する。
  useEffect(() => {
    return () => {
      if (entryActiveResetTimerRef.current !== null) {
        window.clearTimeout(entryActiveResetTimerRef.current);
        entryActiveResetTimerRef.current = null;
      }
    };
  }, []);

  // ── ハンドラ ──────────────────────────────────────
  const handleSlotClick = (index: number) => {
    if (disabled) return;
    if (performance.now() < suppressClickUntilRef.current) return;
    const item = items[index];
    if (!item) return;
    activeIndexRef.current = index;
    setActiveIndex(index);
    onChangeRef.current(item.id, item);
    scrollToCardIndex(index);
    // カード選択直後に Stage へフォーカスを移し、←→↑↓ キーがそのまま
    // カルーセル送りに効くようにする（上位の上下キー操作より優先させる）。
    stageRef.current?.focus({ preventScroll: true });
  };

  /**
   * ダブルクリックは Enter と同じ「決定」として扱う。1・2 回目の click で
   * handleSlotClick（選択 + センタリング）が先に走っているため、ここでは
   * 選択状態を同期したうえで onConfirm を発火するだけでよい。
   */
  const handleSlotDoubleClick = (index: number) => {
    if (disabled) return;
    if (performance.now() < suppressClickUntilRef.current) return;
    const item = items[index];
    if (!item) return;
    activeIndexRef.current = index;
    setActiveIndex(index);
    onChangeRef.current(item.id, item);
    onConfirmRef.current?.(item.id, item);
  };

  /**
   * Stage / ZoomLayer 自体（カードの "外側" の余白）がクリックされたときだけ
   * deselect コールバックを呼ぶ（旧 FollowsCarousel の handleWrapperClick 相当）。
   * Slot 内クリックは Slot 側で処理され、バブリングしてきても
   * `e.target !== e.currentTarget` になるので素通りする。ZoomLayer は Stage を
   * 覆う中間レイヤーなので、両方に同じハンドラを張って従来の挙動を保つ
   * （ZoomLayer 上のクリックが Stage まで届いても target 不一致で二重発火しない）。
   */
  const handleStageBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (disabled) return;
    if (performance.now() < suppressClickUntilRef.current) return;
    if (value === undefined) return;
    onDeselectRef.current?.();
  };

  const handleStageKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || N === 0) return;
    // スタックプレビュー表示中の矢印キーは、document 側のスタック用リスナが
    // 「← / → = 選択の切り替え」として処理する（それ以外の矢印は解除）。
    // Stage フォーカス時にこちらでも送ると 1 打で 2 step 進んでしまうため退避。
    if (
      stackActiveRef.current &&
      (e.key === "ArrowRight" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowUp")
    ) {
      return;
    }
    // ← / → / ↑ / ↓ では smooth scroll を 1 step ぶん発火するだけ。activeIndex /
    // onChange の更新は scroll listener が「視覚中央のカード」と同期するため、ここでは
    // 触らない。これにより押しっぱなしのキーリピートでも「視覚中央 = 選択中」が常に
    // 一致する（即時 setActiveIndex すると aria-checked が先行して中央位置と乖離する）。
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      advanceScrollByOneStep(1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      advanceScrollByOneStep(-1);
    } else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      // 押しっぱなしのキーリピートで決定（遷移・ステップ送り）を連発しない。
      // グローバル側（globalKeyboardNav）の Enter と同じ扱いに揃える。
      if (e.repeat) return;
      // Enter だけは「決定」として通知する（Space は選択の確定のみ）。
      commitActiveItem({ confirm: e.key === "Enter" });
    }
  };

  // ── 描画 ──────────────────────────────────────────
  // breakOut モードでは Stage が position:fixed で flow から外れるため、
  // Wrapper に明示的な高さ（= stageHeight + totalPinDistance）を持たせて
  // OverlayBody 側のスクロール高さを維持する。
  const wrapperExplicitHeight = effectiveBreakOut
    ? stageHeight + totalPinDistance
    : null;

  return (
    <Wrapper
      ref={wrapperRef}
      className={className}
      $explicitHeight={wrapperExplicitHeight}
    >
      <Stage
        ref={stageRef}
        $height={stageHeight}
        $breakOut={effectiveBreakOut}
        role="radiogroup"
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleStageKeyDown}
        onClick={handleStageBackgroundClick}
      >
        <ZoomLayer
          ref={zoomLayerRef}
          $originY={cardCenterY}
          $zoomEnabled={velocityZoomEnabled}
          onClick={handleStageBackgroundClick}
        >
          {items.map((item, i) => {
            const isChecked = value !== undefined && item.id === value;
            return (
              <Slot
                key={item.id}
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
                $width={cardSize.width}
                $height={cardSize.height}
                $offsetTop={cardCenterY}
                role="radio"
                aria-checked={isChecked}
                aria-disabled={disabled || undefined}
                tabIndex={-1}
                onClick={() => handleSlotClick(i)}
                onDoubleClick={() => handleSlotDoubleClick(i)}
              >
                {renderCard(item, {
                  checked: isChecked,
                  index: i,
                  activeIndex,
                })}
              </Slot>
            );
          })}
        </ZoomLayer>
        {stackPreviewEnabled && (
          <StackCountLabel
            ref={stackLabelRef}
            $top={stackLabelTop}
            aria-hidden="true"
          >
            {stackLabelCount}
          </StackCountLabel>
        )}
      </Stage>
      {!effectiveBreakOut && !reducedMotion && (
        <ScrollSpacer $height={totalPinDistance} aria-hidden="true" />
      )}
    </Wrapper>
  );
}

export default RadioCardGroup;
